// React hooks and UI components
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Row, Col, Button, Input, Typography, Space, message, Layout, Modal, Form, Tabs, Select, Dropdown } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  FolderOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  LogoutOutlined,
  ProfileOutlined,
  GiftOutlined,
  QuestionCircleOutlined,
  LockOutlined,
  MinusOutlined,
  StarOutlined,
  ClockCircleOutlined,
  ShareAltOutlined,
  MoreOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { useNoteStore } from '../stores/noteStore';
import '../css/Dashboard.css';
import apiClient from '../services/api';
import fileService from '../services/fileService';
import RightContentPane from '../components/Dashboard/RightContentPane';
import TranslateText from '../components/TranslateText';
import KnowledgeCard from '../components/Dashboard/KnowledgeCard';
import NoteListAsk from '../components/Dashboard/NoteListAsk';
import AIWriterPanel from '../components/Dashboard/AIWriterPanel';
import {
  createDashboardHandlers, createTopicHandlers,
  createCollaborativeHandlers, getQuickActions, apiHelpers, loadNoteContent
} from './DashboardUtils';
import { PrivateKnowledgeBaseProvider } from '../contexts/PrivateKnowledgeBaseContext';
import { CollaborativeKnowledgeBaseProvider } from '../contexts/CollaborativeKnowledgeBaseContext';
import { NoteListProvider, useNoteList } from '../contexts/NoteListContext';
import NoteListPanel from '../components/Dashboard/NoteListPanel';
import { usePrivateKnowledgeBase } from '../contexts/PrivateKnowledgeBaseContext';
import { useCollaborativeKnowledgeBase } from '../contexts/CollaborativeKnowledgeBaseContext';
import DashboardSidebar from '../components/Dashboard/DashboardSidebar';
//import FontContrastComparison from '../components/Dashboard/FontContrastComparison';

// 替换 useDashboardState 为 useDashboardGlobalVar
// Global state management
// Uses a global state system to coordinate between:
// - Search filters (folder, tag, topic, knowledge base)
// - Selected items (notes, search terms)
// - User interactions (manual vs automatic selections)
import {
  getSelectedFolder,
  getSelectedTag,
  getSelectedTopic,
  getSelectedKB,
  getSearchKeyword,
  getSelectedNote,
  getIsUserSelectedNote,
  getPagination,
  getExpandedTopicsArray,
  getExpandedCollaborativeKBsArray,
  setSelectedFolder,
  setSelectedTag,
  setSelectedTopic,
  setSelectedKB,
  setSearchKeyword,
  setSearchMode,
  setSelectedNote,
  setIsUserSelectedNote,
  setPagination,
  setExpandedTopicsArray,
  setExpandedCollaborativeKBsArray,
  updateGlobalState,
  getGlobalState,
  resetGlobalState,
  addGlobalStateListener,
  getNoteStateChange,
  getPendingTopicCheck,
  clearPendingTopicCheck
} from '../hooks/useDashboardGlobalVar';

// 定义分页限制常量，从环境变量读取，默认20
const pageLimit = parseInt(process.env.REACT_APP_FETCH_LIMIT || process.env.FETCH_LIMIT || '20', 10);

const { Search } = Input;
const { Text, Title } = Typography;
const { Option } = Select;
const { Sider, Content } = Layout;

const DonotDisplayZeroCountTopic = true; //WLAN的手工配置处 是否显示笔记数量为0的私有库主题（缺省主题除外）
const DonotDisplayZeroCountTopicForCollaborativeKB = false; //WLAN的手工配置处 是否显示笔记数量为0的协作库主题（缺省主题除外）

// 是否显示主题/子主题的竖形操作菜单（编辑、删除、新建子主题），缺省 false
const enableEditTopic = process.env.REACT_APP_ENABLE_EDIT_TOPIC === 'true';

// 新增：判断是否为文件路径
function isFilePath(content) {
  if (!content) return false;
  return (
    typeof content === 'string' &&
    (content.startsWith('uploads/') || content.startsWith('cloud/')) &&
    (content.endsWith('.md') || content.endsWith('.txt') || content.endsWith('.html') || content.endsWith('.htm'))
  );
}

const DashboardContent = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, token, logout, checkAuth } = useAuthStore();
  const { t } = useTranslation(['dashboard', 'messages', 'common']);

  // 检查 user 对象是否包含 id 字段，如果没有则刷新用户信息
  useEffect(() => {
    if (isAuthenticated && user && !user.id) {
      console.warn('[Dashboard] user 对象缺少 id 字段，尝试刷新用户信息:', user);
      checkAuth();
    }
  }, [isAuthenticated, user, checkAuth]);

  // 使用全局状态管理替代 useDashboardState
  // 获取当前状态值
  const selectedFolder = getSelectedFolder();

  // 本地状态管理选中的笔记和用户选择标志
  // Local State Management
  // Manages local component state for:
  // - Note editing (title, summary, content)
  // - Tag management (adding, editing tags)
  // - UI states (modals, loading, expanded sections)
  // - User preferences (language, interface settings)
  const [selectedNote, setSelectedNote] = useState(getSelectedNote());
  const [isUserSelectedNote, setIsUserSelectedNote] = useState(getIsUserSelectedNote());
  const [, setForceUpdate] = useState(0);

  // 从全局状态获取其他值
  const selectedTag = getSelectedTag();
  const selectedTopic = getSelectedTopic();
  const selectedKB = getSelectedKB();
  const searchKeyword = getSearchKeyword();
  const pagination = getPagination();

  // 使用 useState 来存储展开状态，并在 useEffect 中更新
  const [expandedTopics, setExpandedTopics] = useState(getExpandedTopicsArray());
  const [expandedCollaborativeKBs, setExpandedCollaborativeKBs] = useState(getExpandedCollaborativeKBsArray());

  // 监听全局状态变化，更新本地状态
  useEffect(() => {
    const currentExpandedTopics = getExpandedTopicsArray();
    const currentExpandedCollaborativeKBs = getExpandedCollaborativeKBsArray();

    console.log('[Dashboard] 初始化展开状态:', {
      currentExpandedTopics,
      currentExpandedCollaborativeKBs
    });

    setExpandedTopics(currentExpandedTopics);
    setExpandedCollaborativeKBs(currentExpandedCollaborativeKBs);

    // 设置全局状态监听器，实时监听状态变化
    const removeListener = addGlobalStateListener((newState, oldState, updates) => {
      // 检查 expandedTopicsArray 是否发生变化
      if (updates.expandedTopicsArray) {
        console.log('[Dashboard] 全局状态监听器检测到 expandedTopics 变化:', {
          old: expandedTopics,
          new: newState.expandedTopicsArray
        });
        setExpandedTopics(newState.expandedTopicsArray);
      }

      // 检查 expandedCollaborativeKBsArray 是否发生变化
      if (updates.expandedCollaborativeKBsArray) {
        console.log('[Dashboard] 全局状态监听器检测到 expandedCollaborativeKBs 变化:', {
          old: expandedCollaborativeKBs,
          new: newState.expandedCollaborativeKBsArray
        });
        setExpandedCollaborativeKBs(newState.expandedCollaborativeKBsArray);
      }

      // 检查 selectedFolder 或 searchKeyword 是否发生变化，强制重新渲染
      if (updates.selectedFolder !== undefined || updates.searchKeyword !== undefined) {
        console.log('[Dashboard] 全局状态监听器检测到 selectedFolder 或 searchKeyword 变化，强制重新渲染');

        // 如果切换到了 Playground，折叠右侧面板
        if (newState.selectedFolder === 'playground') {
          setIsRightPanelVisible(false);
        }

        setForceUpdate(prev => prev + 1);
      }

      // 检查 selectedNote 是否发生变化
      if (updates.selectedNote) {
        console.log('[Dashboard] 全局状态监听器检测到 selectedNote 变化:', {
          old: selectedNote,
          new: newState.selectedNote
        });
        setSelectedNote(newState.selectedNote);
      }

      // 检查 isUserSelectedNote 是否发生变化
      if (updates.isUserSelectedNote !== undefined) {
        console.log('[Dashboard] 全局状态监听器检测到 isUserSelectedNote 变化:', {
          old: isUserSelectedNote,
          new: newState.isUserSelectedNote
        });
        setIsUserSelectedNote(newState.isUserSelectedNote);
      }

      // 检查笔记状态变化，强制重新渲染
      if (updates.noteStateChange) {
        console.log('[Dashboard] 全局状态监听器检测到笔记状态变化:', newState.noteStateChange);

        // 根据变化类型处理不同的逻辑
        const { changeType, noteId } = newState.noteStateChange;

        if (changeType === 'update' && noteId && selectedNote && selectedNote.id === noteId) {
          console.log('[Dashboard] 检测到当前选中笔记的更新，重新获取主题层级信息');

          // 延迟执行，确保笔记数据已更新
          setTimeout(async () => {
            try {
              // 重新获取笔记的主题层级信息
              await fetchNoteTopicHierarchy(noteId);
              console.log('[Dashboard] 笔记更新后，主题层级信息已重新获取');

              // 如果当前有展开的主题，也需要刷新相关数据
              if (getSelectedTopic() || getSelectedKB()) {
                console.log('[Dashboard] 检测到当前有主题或知识库筛选，刷新相关数据');
                await refreshKnowledgeBasesAndTopics();
              }
            } catch (error) {
              console.error('[Dashboard] 重新获取主题层级信息失败:', error);
            }
          }, 500); // 延迟500ms，确保后端处理完成
        }

        // 强制重新渲染，触发置顶按钮状态更新
        setSelectedNote(prev => {
          console.log('[Dashboard] 强制重新渲染笔记状态:', {
            prevId: prev?.id,
            prevSummary: prev?.summary?.substring(0, 100),
            prevTitle: prev?.title
          });
          return { ...prev };
        });
      }
    });

    console.log('[Dashboard] 成功设置全局状态监听器，移除函数类型:', typeof removeListener);

    // 清理监听器
    return removeListener;
  }, []); // 只在组件挂载时执行一次


  // Get note list context
  const {
    notes,
    pinnedNotes,
    searchResults, displayNotes, isLoading, isSearchLoading, fetchNotes, updateNote, deleteNote, resetState,
    noteListRef, clearSearchResults, loadMoreNotes, setNoteOperationCallback, executeNoteOperationCallback,
    handleDeleteNote
  } = useNoteList();

  // Get note store for togglePinNote
  const { togglePinNote } = useNoteStore();

  // Get private knowledge base context
  const { fetchPrivateKBTopics, topicNotesCount: privateTopicNotesCount, hasPrivateKB, privateTopics, setPrivateTopics, fetchTopicNotesCountForTree: fetchPrivateTopicNotesCount, setTopicNotesCount: setPrivateTopicNotesCount } = usePrivateKnowledgeBase();

  // Get collaborative knowledge base context
  const {
    fetchUserCollaborativeKBs,
    topicNotesCount: collaborativeTopicNotesCount,
    collaborativeKBs,
    toggleCollaborativeKBExpanded,
    collaborativeKBTopics,
    setCollaborativeKBTopics,
    handleDeleteCollaborativeTopic,
    setIsCreateCollaborativeVisible,
    handleCreateCollaborative: contextHandleCreateCollaborative,
    isCreateCollaborativeVisible,
    handleStartCreateCollaborativeKBTopic,
    handleShareCollaborativeKB,
    handleViewMembers,
    handleRenameCollaborativeKB,
    handleConfirmRenameCollaborativeKB,
    handleCancelRenameCollaborativeKB,
    handleLeaveCollaborativeKB,
    fetchCollaborativeKBTopics,
    fetchTopicNotesCountForTree,
    setTopicNotesCount: setCollaborativeTopicNotesCount
  } = useCollaborativeKnowledgeBase();

  // 合并私有库和协作库的笔记数量
  const topicNotesCount = { ...privateTopicNotesCount, ...collaborativeTopicNotesCount };

  // 协作库笔记数量缓存
  const [kbNotesCountCache, setKbNotesCountCache] = useState({});

  // 批量获取所有协作库的笔记数量（需要在 refreshKnowledgeBasesAndTopics 之前定义）
  const fetchAllCollaborativeKBNotesCount = useCallback(async () => {
    if (!collaborativeKBs || collaborativeKBs.length === 0) {
      return;
    }

    try {
      console.log('[Dashboard] 开始批量获取协作库笔记数量...');
      const countPromises = collaborativeKBs.map(async (kb) => {
        try {
          const count = await apiHelpers.fetchKbNotesCount(kb.kb_id);
          return { kbId: kb.kb_id, count };
        } catch (error) {
          console.error(`[Dashboard] 获取协作库 ${kb.kb_id} 笔记数量失败:`, error);
          return { kbId: kb.kb_id, count: 0 };
        }
      });

      const results = await Promise.all(countPromises);
      const newCache = {};
      results.forEach(({ kbId, count }) => {
        newCache[kbId] = count;
      });

      setKbNotesCountCache(newCache);
      console.log('[Dashboard] 协作库笔记数量缓存更新完成:', newCache);
    } catch (error) {
      console.error('[Dashboard] 批量获取协作库笔记数量失败:', error);
    }
  }, [collaborativeKBs]);

  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editSummary, setEditSummary] = useState('');
  const [editContent, setEditContent] = useState('');
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
  /** 打开 New Note 弹窗时是否同时弹出 AI Writer 助手，缺省 false；可通过 handleOpenCreateModal(enableAIWriter) 传入 */
  const [createModalEnableAIWriter, setCreateModalEnableAIWriter] = useState(false);
  const [createForm] = Form.useForm();
  const [linkForm] = Form.useForm();
  const [isCreatingNote, setIsCreatingNote] = useState(false);
  const [createTimer, setCreateTimer] = useState(0);
  const [timerInterval, setTimerInterval] = useState(null);
  const [createMode, setCreateMode] = useState('manual'); // 添加创建模式状态
  const [isAddingTag, setIsAddingTag] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [userHistoryTags, setUserHistoryTags] = useState([]);
  const [isLoadingHistoryTags, setIsLoadingHistoryTags] = useState(false);
  const [editingTopic, setEditingTopic] = useState(null); // 当前编辑的主题ID (for collaborative topics)
  const [editingTopicName, setEditingTopicName] = useState(''); // 编辑中的主题名称 (for collaborative topics)
  const [editingTopicContext, setEditingTopicContext] = useState(null); // 编辑主题的上下文信息 (for collaborative topics)
  const [editingCollaborativeKB, setEditingCollaborativeKB] = useState(null); // 当前编辑的协作库ID
  const [editingCollaborativeKBName, setEditingCollaborativeKBName] = useState(''); // 编辑中的协作库名称
  const [isEditTopicVisible, setIsEditTopicVisible] = useState(false); // 编辑主题模态框显示状态
  const [editTopicForm] = Form.useForm(); // 编辑主题表单
  const [isCreateTopicVisible, setIsCreateTopicVisible] = useState(false); // 新建主题模态框显示状态
  const [createTopicForm] = Form.useForm(); // 新建主题表单
  const [isCreateSubTopicVisible, setIsCreateSubTopicVisible] = useState(false); // 新建子主题模态框显示状态
  const [createSubTopicForm] = Form.useForm(); // 新建子主题表单
  const [selectedParentTopic, setSelectedParentTopic] = useState(null); // 选中的父主题
  const [noteTopicHierarchy, setNoteTopicHierarchy] = useState(null); // 当前笔记的主题层级信息
  const [lastFetchedNoteId, setLastFetchedNoteId] = useState(null); // 上次获取主题层级信息的笔记ID
  const [isContentExpanded, setIsContentExpanded] = useState(false); // 内容是否展开

  // 主题选择器相关状态

  // 新建笔记时的笔记库选择状态
  const [createNoteKnowledgeBase, setCreateNoteKnowledgeBase] = useState(null); // 新建笔记时选中的笔记库
  const [createAvailableKnowledgeBases, setCreateAvailableKnowledgeBases] = useState([]); // 新建笔记时可用的笔记库列表

  const [leftmostSiderWidth] = useState(280); // Leftmost fixed Sider width
  const MIN_NOTE_LIST_WIDTH = 300;
  const MIN_PREVIEW_WIDTH = 500;

  // Adaptive initial widths based on screen resolution
  const getAdaptiveInitialWidths = useCallback((availableWidth) => {
    // Small screens (< 1400px): Preview gets 50% of available width, min 600px
    if (availableWidth < 1400) {
      const previewWidth = Math.max(Math.floor(availableWidth * 0.5), 600);
      const noteListWidth = availableWidth - previewWidth;
      return {
        previewWidth: Math.max(previewWidth, MIN_PREVIEW_WIDTH),
        noteListWidth: Math.max(noteListWidth, MIN_NOTE_LIST_WIDTH)
      };
    }
    // Medium screens (1400px - 1920px): Preview gets 400px, note list gets remaining
    else if (availableWidth < 1920) {
      return {
        previewWidth: 500,
        noteListWidth: availableWidth - 500
      };
    }
    // Large screens (1920px - 2560px): Preview gets 40% of available width, max 600px
    else if (availableWidth < 2560) {
      const previewWidth = Math.min(Math.floor(availableWidth * 0.3), 600);
      return {
        previewWidth,
        noteListWidth: availableWidth - previewWidth
      };
    }
    // Extra large screens (>= 2560px): Preview gets 35% of available width, max 800px
    else {
      const previewWidth = Math.min(Math.floor(availableWidth * 0.3), 800);
      return {
        previewWidth,
        noteListWidth: availableWidth - previewWidth
      };
    }
  }, []);

  const [noteListWidth, setNoteListWidth] = useState(null); // Will be calculated as remaining space
  const [maxNoteListSiderWidth, setMaxNoteListSiderWidth] = useState(1200); // Fallback max width, will be calculated
  const [isRightPanelVisible, setIsRightPanelVisible] = useState(true); // Toggle for right panel visibility (default: visible)
  const [rightPanelMode, setRightPanelMode] = useState('note-detail'); // Right panel mode: 'note-detail' or 'ai-ask'
  const [shouldAutoTriggerQA, setShouldAutoTriggerQA] = useState(false); // Flag to control whether to auto-trigger QA when switching to ai-ask mode

  const resizablePanesContainerRef = useRef(null); // Ref for the Layout containing note list and preview
  const qaContentRef = useRef(null); // Ref for QA content area in NoteListAsk
  const lastSearchKeywordRef = useRef(null); // Track last search keyword to detect changes
  const [qaInitialQuestion, setQaInitialQuestion] = useState(null); // Initial question for QA component
  const isSearchTriggeredRef = useRef(false); // Track if mode change is triggered by search
  const [isDragging, setIsDragging] = useState(false);

  // 笔记统计数据状态
  const [notesStats, setNotesStats] = useState(null); // 笔记统计数据

  // 文件上传相关状态
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadedFileUrls, setUploadedFileUrls] = useState([]);
  const [isBatchCreating, setIsBatchCreating] = useState(false);
  const [batchResult, setBatchResult] = useState(null);

  const [realContent, setRealContent] = useState('');
  const [isLoadingContent, setIsLoadingContent] = useState(false);

  // 1. 新增状态
  const [editTooltipVisible, setEditTooltipVisible] = useState(false);

  // 新增：手动输入内容状态
  const [manualInputContent, setManualInputContent] = useState('');

  // 添加一个状态来跟踪是否是用户手动选择的笔记



  // 标签页相关状态
  const [activeTabKey, setActiveTabKey] = useState('content');
  // 获取用户自定义标签的函数
  const fetchUserCustomTags = useCallback(async () => {
    if (!user?.id) return;
    const tags = await apiHelpers.fetchUserCustomTags();
    // 不再需要存储到状态，直接返回
    return tags;
  }, [user?.id]);

  // 获取笔记统计数据
  const fetchNotesStats = useCallback(async () => {
    try {
      console.log('[前端] 开始获取笔记统计数据...');
      const stats = await apiHelpers.fetchNotesStats();
      console.log('[前端] 获取到原始统计数据:', stats);
      setNotesStats(stats);
      console.log('[前端] 笔记统计数据更新完成，新状态:', stats);
    } catch (error) {
      console.error('[前端] 获取笔记统计数据失败:', error);
      // 设置默认值
      const defaultStats = {
        totalNotes: 0,
        privateNotes: 0,
        collaborativeNotes: 0,
        pinnedNotes: 0,
        recentNotes: 0,
        publicNotes: 0
      };
      setNotesStats(defaultStats);
      console.log('[前端] 设置默认统计数据:', defaultStats);
    }
  }, []); // 空依赖数组，因为函数内部只使用了稳定的 API 调用

  // 获取当前筛选状态的辅助函数
  const getCurrentFilterState = useCallback(() => {
    return {
      selectedFolder: getSelectedFolder(),
      selectedTag: getSelectedTag(),
      selectedTopic: getSelectedTopic(),
      selectedKB: getSelectedKB(),
      searchKeyword: getSearchKeyword()
    };
  }, []);

  // 重置筛选器的函数
  const resetFilters = useCallback(() => {
    console.log('[Dashboard] 重置筛选器');
    updateGlobalState({
      selectedFolder: 'all',
      selectedTag: null,
      selectedTopic: null,
      selectedKB: null,
      searchKeyword: ''
    }, 'Dashboard-resetFilters');
  }, []);
  useEffect(() => {
    if (!isAuthenticated || !token) {
      console.log('用户未认证，重定向到登录页面');
      navigate('/login');
      return;
    }

    // 检查是否需要清空状态（登录成功后）
    const shouldClearState = sessionStorage.getItem('shouldClearStateOnMount');
    if (shouldClearState === 'true') {
      console.log('[Dashboard] 检测到登录成功标志，清空所有状态');
      sessionStorage.removeItem('shouldClearStateOnMount');
      
      // 重置全局状态
      resetGlobalState('Dashboard-after-login');
      
      // 确保搜索框内容被清空
      setSearchKeyword('', 'Dashboard-after-login');
      console.log('[Dashboard] 已清空搜索框内容和全局状态');
    }

    // 检查是否需要重置到指定的知识库（新建笔记后刷新）
    const shouldResetToAllNotes = localStorage.getItem('resetToAllNotesAfterCreate');
    if (shouldResetToAllNotes === 'true') {
      console.log('[Dashboard] 检测到新建笔记后的刷新');
      localStorage.removeItem('resetToAllNotesAfterCreate');
      
      // 检查是否有存储的知识库信息
      const storedSelectedKB = localStorage.getItem('createNoteSelectedKB');
      const storedSelectedFolder = localStorage.getItem('createNoteSelectedFolder');
      
      if (storedSelectedKB) {
        // 如果有存储的协作库信息，设置sidebar为协作库
        try {
          const kbInfo = JSON.parse(storedSelectedKB);
          console.log('[Dashboard] 恢复sidebar为协作库:', kbInfo.name);
          
          // 需要等待协作库列表加载完成后再设置
          // 先确保协作库列表已加载
          fetchUserCollaborativeKBs().then(() => {
            // 延迟一下，确保状态已更新
            setTimeout(() => {
              // 从全局状态或context中查找协作库
              const targetKB = collaborativeKBs?.find(kb => kb.kb_id === kbInfo.kb_id);
              if (targetKB) {
                console.log('[Dashboard] 找到协作库，设置sidebar:', targetKB.name);
                // 使用updateGlobalState直接设置，避免setSelectedKB将selectedFolder设置为'all'
                updateGlobalState({
                  selectedKB: targetKB,
                  selectedFolder: 'kb', // 设置为'kb'表示选中知识库，而不是'all'
                  selectedTopic: null,
                  selectedTag: null
                }, 'Dashboard-resetAfterCreate');
              } else {
                // 如果找不到，使用存储的信息创建一个临时对象
                console.log('[Dashboard] 未找到协作库，使用存储的信息创建临时对象');
                updateGlobalState({
                  selectedKB: { kb_id: kbInfo.kb_id, name: kbInfo.name, is_private: false },
                  selectedFolder: 'kb',
                  selectedTopic: null,
                  selectedTag: null
                }, 'Dashboard-resetAfterCreate');
              }
            }, 300);
          }).catch(error => {
            console.error('[Dashboard] 加载协作库列表失败:', error);
            // 如果加载失败，使用存储的信息创建临时对象
            updateGlobalState({
              selectedKB: { kb_id: kbInfo.kb_id, name: kbInfo.name, is_private: false },
              selectedFolder: 'kb',
              selectedTopic: null,
              selectedTag: null
            }, 'Dashboard-resetAfterCreate');
          });
        } catch (error) {
          console.error('[Dashboard] 解析存储的协作库信息失败:', error);
          resetFilters();
        }
        localStorage.removeItem('createNoteSelectedKB');
        // 设置标志，表示已经处理了恢复逻辑，避免else分支执行resetFilters
        localStorage.setItem('hasProcessedCreateNoteReset', 'true');
      } else if (storedSelectedFolder === 'private') {
        // 如果有存储的私有库信息，设置sidebar为"Private Library"
        console.log('[Dashboard] 恢复sidebar为私有库');
        setSelectedFolder('private', 'Dashboard-resetAfterCreate');
        localStorage.removeItem('createNoteSelectedFolder');
        // 设置标志，表示已经处理了恢复逻辑，避免else分支执行resetFilters
        localStorage.setItem('hasProcessedCreateNoteReset', 'true');
      } else {
        // 如果没有存储的信息，重置到 "All Notes"
        console.log('[Dashboard] 没有存储的知识库信息，重置到 All Notes');
        resetFilters();
        // 设置标志，表示已经处理了恢复逻辑
        localStorage.setItem('hasProcessedCreateNoteReset', 'true');
      }
    } else {
      // 初始化时加载数据，但保持用户的选择状态
      // 检查是否已经处理过创建笔记后的重置逻辑
      const hasProcessed = localStorage.getItem('hasProcessedCreateNoteReset');
      if (hasProcessed === 'true') {
        // 如果已经处理过，清除标志，不执行resetFilters
        localStorage.removeItem('hasProcessedCreateNoteReset');
        console.log('[Dashboard] 已处理创建笔记后的重置逻辑，跳过resetFilters');
      } else {
        const currentFilters = getCurrentFilterState();
        if (currentFilters.selectedFolder === 'all' && !currentFilters.searchKeyword) {
          // 只有在没有筛选条件时才重置
          resetFilters();
        }
      }
    }

    // 延迟加载，确保状态稳定
    setTimeout(() => {
      fetchNotes(1, pageLimit, [], true, null, false, 'all');
      fetchUserCustomTags();
      fetchNotesStats();
      fetchPrivateKBTopics();
      fetchUserCollaborativeKBs();
    }, 100);
  }, [isAuthenticated, token, navigate, getCurrentFilterState, resetFilters, fetchUserCustomTags, fetchNotesStats, fetchPrivateKBTopics, fetchUserCollaborativeKBs]); // 移除 fetchNotes 依赖，避免无限循环

  // 获取笔记的主题层级信息
  const fetchNoteTopicHierarchy = useCallback(async (noteId) => {
    if (!noteId) {
      setNoteTopicHierarchy(null);
      return;
    }

    try {
      const response = await apiClient.get(`/notes/${noteId}/topics`);
      // 现在存储所有主题关联
      if (response.data && response.data.length > 0) {
        setNoteTopicHierarchy(response.data); // 存储所有主题关联
      } else {
        setNoteTopicHierarchy(null);
      }
    } catch (error) {
      console.error('获取笔记主题信息失败:', error);
      setNoteTopicHierarchy(null);
    }
  }, [setNoteTopicHierarchy]);

  // 获取用户所有笔记库
  const fetchAllKnowledgeBases = async () => {
    try {
      // 使用新的API获取用户所有关联的笔记库
      const allKBs = await apiHelpers.fetchUserAllKnowledgeBases();
      const kbs = [];

      for (const kb of allKBs) {
        if (kb.is_private) {
          // 私有库：获取主题数据
          const privateResult = await apiHelpers.fetchPrivateKBTopics();
          kbs.push({
            kb_id: kb.kb_id,
            name: 'Private KB',
            is_private: true,
            topics: privateResult.topics || []
          });
        } else {
          // 协作库：获取主题数据
          const topics = await apiHelpers.fetchCollaborativeKBTopics(kb.kb_id);
          kbs.push({
            kb_id: kb.kb_id,
            name: kb.name,
            is_private: false,
            topics: topics
          });
        }
      }

      return kbs;
    } catch (error) {
      console.error('获取笔记库列表失败:', error);
      return [];
    }
  };
  // 初始化新建笔记时的笔记库选择器
  const initializeCreateNoteSelectors = async () => {
    const kbs = await fetchAllKnowledgeBases();
    setCreateAvailableKnowledgeBases(kbs);

    // 优先选择第一个私有库，如果没有私有库则选择第一个笔记库
    if (kbs && kbs.length > 0) {
      const privateKB = kbs.find(kb => kb.is_private);
      if (privateKB) {
        setCreateNoteKnowledgeBase(privateKB);
        console.log('[前端] 默认选择第一个私有库:', privateKB);
      } else {
        setCreateNoteKnowledgeBase(kbs[0]);
        console.log('[前端] 未找到私有库，选择第一个笔记库:', kbs[0]);
      }
    } else {
      console.log('[前端] 未找到任何笔记库');
    }
  };

  // 处理新建笔记时的笔记库选择变化
  const handleCreateNoteKnowledgeBaseChange = (kbId) => {
    const kb = createAvailableKnowledgeBases.find(k => k.kb_id === kbId);
    setCreateNoteKnowledgeBase(kb);
  };

  // 重置新建笔记的选择器状态
  const resetCreateNoteSelectors = () => {
    setCreateNoteKnowledgeBase(null);
    setCreateAvailableKnowledgeBases([]);
  };

  // 统一的刷新左侧笔记库和主题数据的函数
  const refreshKnowledgeBasesAndTopics = useCallback(async () => {
    console.log('[Dashboard] 开始刷新笔记库和主题数据...');

    try {
      // 并行刷新私有库、协作库列表和统计数据
      const [statsResult, privateTopicsResult] = await Promise.all([
        fetchNotesStats().catch(err => {
          console.error('[Dashboard] 刷新统计数据失败:', err);
          return null;
        }),
        fetchPrivateKBTopics().catch(err => {
          console.error('[Dashboard] 刷新私有库主题数据失败:', err);
          return null;
        })
      ]);

      console.log('[Dashboard] 基础数据刷新完成:', {
        statsResult: statsResult ? '成功' : '失败',
        privateTopicsResult: privateTopicsResult ? '成功' : '失败'
      });

      // 刷新协作库数据 - 通过协作库Context的刷新函数
      try {
        console.log('[Dashboard] 开始刷新协作库数据...');
        // 使用协作库Context提供的刷新函数
        if (window.refreshCollaborativeKBData) {
          await window.refreshCollaborativeKBData();
          console.log('[Dashboard] 协作库数据刷新完成');
        } else {
          console.warn('[Dashboard] 协作库刷新函数不可用，跳过协作库数据刷新');
        }
      } catch (error) {
        console.error('[Dashboard] 刷新协作库数据失败:', error);
        // 不阻断其他数据的刷新
      }

      // 刷新协作库笔记数量
      try {
        console.log('[Dashboard] 开始刷新协作库笔记数量...');
        await fetchAllCollaborativeKBNotesCount();
        console.log('[Dashboard] 协作库笔记数量刷新完成');
      } catch (error) {
        console.error('[Dashboard] 刷新协作库笔记数量失败:', error);
        // 不阻断其他数据的刷新
      }

      // 刷新主题笔记数量统计 - 确保sidebar显示正确的笔记数量
      try {
        console.log('[Dashboard] 开始刷新主题笔记数量统计...');
        const refreshPromises = [];

        // 刷新私有库主题笔记数量
        if (fetchPrivateTopicNotesCount && privateTopics && privateTopics.length > 0) {
          refreshPromises.push(
            fetchPrivateTopicNotesCount(privateTopics).then(() => {
              console.log('[Dashboard] 私有库主题笔记数量刷新完成');
            }).catch(err => {
              console.error('[Dashboard] 刷新私有库主题笔记数量失败:', err);
            })
          );
        }

        // 刷新协作库主题笔记数量
        if (fetchTopicNotesCountForTree && collaborativeKBTopics) {
          for (const [kbId, topics] of Object.entries(collaborativeKBTopics)) {
            if (topics && topics.length > 0) {
              refreshPromises.push(
                fetchTopicNotesCountForTree(topics).then(() => {
                  console.log(`[Dashboard] 协作库 ${kbId} 主题笔记数量刷新完成`);
                }).catch(err => {
                  console.error(`[Dashboard] 刷新协作库 ${kbId} 主题笔记数量失败:`, err);
                })
              );
            }
          }
        }

        if (refreshPromises.length > 0) {
          await Promise.all(refreshPromises);
          console.log('[Dashboard] 主题笔记数量统计刷新完成');
        }
      } catch (error) {
        console.error('[Dashboard] 刷新主题笔记数量时发生错误:', error);
        // 不阻断其他数据的刷新
      }

      // 强制触发一次状态更新，确保UI重新渲染
      console.log('[Dashboard] 强制触发状态更新...');

      // 如果当前有选中的笔记，重新获取其主题层级信息
      if (selectedNote) {
        try {
          await fetchNoteTopicHierarchy(selectedNote.id);
          console.log('[Dashboard] 已重新获取选中笔记的主题层级信息');
        } catch (error) {
          console.error('[Dashboard] 重新获取主题层级信息失败:', error);
        }
      }

      console.log('[Dashboard] 笔记库和主题数据刷新完成');
    } catch (error) {
      console.error('[Dashboard] 刷新笔记库和主题数据失败:', error);
      message.error(t('messages:error_loading', { defaultValue: 'Error loading data' }));
    }
  }, [fetchNotesStats, fetchPrivateKBTopics, selectedNote, fetchNoteTopicHierarchy, fetchPrivateTopicNotesCount, fetchTopicNotesCountForTree, privateTopics, collaborativeKBTopics, fetchAllCollaborativeKBNotesCount]);

  // 将刷新函数暴露到全局，供协作库Context调用
  React.useEffect(() => {
    window.refreshKnowledgeBasesAndTopics = refreshKnowledgeBasesAndTopics;

    // 清理函数
    return () => {
      delete window.refreshKnowledgeBasesAndTopics;
    };
  }, [refreshKnowledgeBasesAndTopics]);

  // 监听 pendingTopicCheck 全局状态变化，自动刷新主题树
  // 用于支持 DonotDisplayZeroCountTopic=true 时，当笔记操作导致主题有笔记后自动显示该主题
  useEffect(() => {
    // 设置全局状态监听器，监听 pendingTopicCheck 变化
    const removeListener = addGlobalStateListener((newState, oldState, updates) => {
      // 检查 pendingTopicCheck 是否发生变化
      if (updates.pendingTopicCheck && newState.pendingTopicCheck) {
        const pendingCheck = newState.pendingTopicCheck;

        console.log('[Dashboard] 检测到 pendingTopicCheck 变化:', pendingCheck);

        // 检查是否有需要刷新的主题
        if (Object.keys(pendingCheck).length === 0) {
          return;
        }

        // 使用防抖处理，避免频繁刷新
        const checkAndRefreshTopics = async () => {
          const kbsToRefresh = new Set(); // 需要刷新的知识库集合

          for (const [kbId, topicIds] of Object.entries(pendingCheck)) {
            if (!topicIds || topicIds.length === 0) continue;

            // 检查主题是否在当前显示的主题树中
            let topicTree = null;

            if (kbId === 'private') {
              // 私有库主题
              topicTree = privateTopics;
            } else {
              // 协作库主题
              topicTree = collaborativeKBTopics[kbId];
            }

            // 如果没有主题树数据，需要刷新
            if (!topicTree || topicTree.length === 0) {
              kbsToRefresh.add(kbId);
              continue;
            }

            // 递归检查主题是否在树中
            const isTopicInTree = (topicId, tree) => {
              for (const topic of tree) {
                if (topic.topic_id === topicId) return true;
                if (topic.children && topic.children.length > 0) {
                  if (isTopicInTree(topicId, topic.children)) return true;
                }
              }
              return false;
            };

            // 检查每个主题是否在当前显示的主题树中
            for (const topicId of topicIds) {
              // 如果 DonotDisplayZeroCountTopic=true，检查主题是否因为笔记数为 0 而被隐藏
              if (DonotDisplayZeroCountTopic) {
                const count = topicNotesCount[topicId];
                // 如果主题不在树中，或者笔记数量从 0 变为 >0，需要刷新
                if (!isTopicInTree(topicId, topicTree) || (count === undefined || count === 0)) {
                  kbsToRefresh.add(kbId);
                  break;
                }
              }
            }
          }

          // 执行刷新
          if (kbsToRefresh.size > 0) {
            console.log('[Dashboard] 需要刷新的知识库:', Array.from(kbsToRefresh));

            const refreshPromises = [];

            for (const kbId of kbsToRefresh) {
              if (kbId === 'private') {
                // 刷新私有库主题树
                refreshPromises.push(
                  fetchPrivateKBTopics().then(() => {
                    console.log('[Dashboard] 私有库主题树刷新完成');
                  }).catch(err => {
                    console.error('[Dashboard] 刷新私有库主题树失败:', err);
                  })
                );
              } else {
                // 刷新协作库主题树
                refreshPromises.push(
                  fetchCollaborativeKBTopics(kbId).then(() => {
                    console.log(`[Dashboard] 协作库 ${kbId} 主题树刷新完成`);
                  }).catch(err => {
                    console.error(`[Dashboard] 刷新协作库 ${kbId} 主题树失败:`, err);
                  })
                );
              }
            }

            await Promise.all(refreshPromises);
            console.log('[Dashboard] 所有需要刷新的主题树刷新完成');
          }

          // 清空 pendingTopicCheck
          clearPendingTopicCheck('Dashboard.pendingTopicCheckListener');
        };

        // 延迟执行，避免在笔记列表更新过程中频繁触发
        setTimeout(checkAndRefreshTopics, 500);
      }
    });

    console.log('[Dashboard] 成功设置 pendingTopicCheck 监听器');

    // 清理监听器
    return removeListener;
  }, [privateTopics, collaborativeKBTopics, topicNotesCount, fetchPrivateKBTopics, fetchCollaborativeKBTopics]);

  // 刷新单个主题的笔记数量（仅刷新选中的主题）
  const refreshSingleTopicNotesCount = useCallback(async (topic, context = null) => {
    try {
      // 只刷新当前选中主题的笔记数量
      const count = await apiHelpers.fetchTopicNotesCount(topic.topic_id);

      if (context && context.type === 'collaborative' && context.kbId) {
        // 协作库主题：只更新当前主题的笔记数量
        setCollaborativeTopicNotesCount(prevCounts => ({
          ...prevCounts,
          [topic.topic_id]: count
        }));
        console.log(`[refreshSingleTopicNotesCount] 协作库主题 ${topic.topic_id} 笔记数量已更新:`, count);
      } else {
        // 私有库主题：只更新当前主题的笔记数量
        setPrivateTopicNotesCount(prevCounts => ({
          ...prevCounts,
          [topic.topic_id]: count
        }));
        console.log(`[refreshSingleTopicNotesCount] 私有库主题 ${topic.topic_id} 笔记数量已更新:`, count);
      }
    } catch (error) {
      console.error(`[refreshSingleTopicNotesCount] 刷新主题 ${topic.topic_id} 笔记数量失败:`, error);
    }
  }, [setPrivateTopicNotesCount, setCollaborativeTopicNotesCount]);

  // 处理主题点击，筛选相关笔记
  const handleTopicClick = useCallback(async (topic, context = null) => {
    console.log('[前端] 主题点击:', topic, 'context:', context);

    // 检查新主题和当前主题是否一致
    if (getSelectedTopic() && getSelectedTopic().topic_id === topic.topic_id) {
      console.log('[handleTopicClick] 当前主题已选中，跳过数据获取');
      return;
    }

    // 使用全局状态更新函数
    updateGlobalState({
      selectedFolder: 'topic',
      selectedTopic: topic,
      selectedKB: null,
      selectedTag: null,
      searchMode: 'hybrid',   // 这里强制设置为缺省的混合检索模式。 WLAN 08.21
      searchKeyword: getSearchKeyword()
    }, 'Dashboard-handleTopicClick');

    try {
      // 立即加载该主题及其子主题的笔记
      await fetchNotes(1, pageLimit, [], true, topic.topic_id, false, 'all');

      // fetchNotes 成功后，仅刷新当前选中主题的笔记数统计
      console.log('[handleTopicClick] fetchNotes 成功，开始刷新当前主题笔记数统计...');

      // 只刷新当前选中主题的笔记数量（不刷新 notesStats 和其他主题）
      await refreshSingleTopicNotesCount(topic, context);

      console.log('[handleTopicClick] 当前主题笔记数统计刷新完成');
    } catch (error) {
      console.error('[handleTopicClick] fetchNotes 失败:', error);
    }
  }, [fetchNotes, refreshSingleTopicNotesCount]); // 简化依赖

  // 刷新单个知识库的笔记数量（仅刷新选中的知识库）
  const refreshSingleKBNotesCount = useCallback(async (kb) => {
    try {
      if (kb.is_private) {
        // 私有库：刷新私有库主题笔记数量
        if (fetchPrivateTopicNotesCount && privateTopics && privateTopics.length > 0) {
          await fetchPrivateTopicNotesCount(privateTopics).catch(err => {
            console.error('[refreshSingleKBNotesCount] 刷新私有库主题笔记数量失败:', err);
          });
        }
      } else {
        // 协作库：只刷新当前知识库的笔记数量和主题笔记数量
        // 刷新当前协作库的笔记数量（只更新缓存中的这一个知识库）
        try {
          const count = await apiHelpers.fetchKbNotesCount(kb.kb_id);
          setKbNotesCountCache(prev => ({
            ...prev,
            [kb.kb_id]: count
          }));
          console.log(`[refreshSingleKBNotesCount] 协作库 ${kb.kb_id} 笔记数量已更新:`, count);
        } catch (error) {
          console.error(`[refreshSingleKBNotesCount] 获取协作库 ${kb.kb_id} 笔记数量失败:`, error);
        }

        // 刷新当前协作库的主题笔记数量
        //if (fetchTopicNotesCountForTree && collaborativeKBTopics && collaborativeKBTopics[kb.kb_id]) {
        //  await fetchTopicNotesCountForTree(collaborativeKBTopics[kb.kb_id]).catch(err => {
        //    console.error('[refreshSingleKBNotesCount] 刷新协作库主题笔记数量失败:', err);
        //  });
        //}
      }
    } catch (error) {
      console.error('[refreshSingleKBNotesCount] 刷新知识库笔记数量时发生错误:', error);
    }
  }, [fetchPrivateTopicNotesCount, fetchTopicNotesCountForTree, privateTopics, collaborativeKBTopics]);

  // 处理知识库点击，筛选相关笔记
  const handleKBClick = useCallback(async (kb) => {
    console.log('[Dashboard] 知识库点击:', kb);

    // 检查新知识库和当前知识库是否一致
    if (getSelectedKB() && getSelectedKB().kb_id === kb.kb_id) {
      console.log('[handleKBClick] 当前知识库已选中，跳过数据获取');
      return;
    }

    // 使用全局状态更新函数
    updateGlobalState({
      selectedFolder: 'kb',
      selectedKB: kb,
      selectedTopic: null,
      selectedTag: null,
      searchMode: 'hybrid',   // 这里强制设置为缺省的混合检索模式。 WLAN 08.21
      searchKeyword: getSearchKeyword()
    }, 'Dashboard-handleKBClick');

    try {
      // 立即加载该知识库的所有笔记
      await fetchNotes(1, pageLimit, [], true, null, false, 'all', null, kb.kb_id);

      // fetchNotes 成功后，仅刷新当前选中知识库的笔记数统计
      console.log('[handleKBClick] fetchNotes 成功，开始刷新当前知识库笔记数统计...');

      // 只刷新当前选中知识库的笔记数量（不刷新 notesStats 和其他知识库）
      await refreshSingleKBNotesCount(kb);

      console.log('[handleKBClick] 当前知识库笔记数统计刷新完成');
    } catch (error) {
      console.error('[handleKBClick] fetchNotes 失败:', error);
    }
  }, [fetchNotes, refreshSingleKBNotesCount]); // 简化依赖

  useEffect(() => {
    resetState(); // 先重置状态
    setTimeout(() => {
      fetchNotes(1, pageLimit, [], true, null, false, 'all'); // 延迟一下再加载，确保状态重置完成
      fetchUserCustomTags(); // 加载用户自定义标签
      fetchNotesStats(); // 加载笔记统计数据

      // 加载私有库和协作库数据
      fetchPrivateKBTopics(); // 加载私有库主题
      fetchUserCollaborativeKBs(); // 加载协作库列表
    }, 100);
  }, []); // 移除fetchNotes依赖，只在组件挂载时执行一次

  // 使用 ref 来存储最新的 refreshKnowledgeBasesAndTopics 函数，避免无限循环
  const refreshKnowledgeBasesAndTopicsRef = useRef();
  refreshKnowledgeBasesAndTopicsRef.current = refreshKnowledgeBasesAndTopics;

  // 设置笔记操作完成后的回调函数，用于更新 sidebar 统计数据
  useEffect(() => {
    if (setNoteOperationCallback) {
      console.log('[Dashboard] 设置笔记操作完成回调函数');
      setNoteOperationCallback({
        onOperationComplete: () => {
          console.log('[Dashboard] 笔记操作完成，更新 sidebar 统计数据');
          fetchNotesStats();
          // 使用 ref 中的最新函数，避免依赖问题
          if (refreshKnowledgeBasesAndTopicsRef.current) {
            refreshKnowledgeBasesAndTopicsRef.current();
          }
        },
        onNoteSelect: (note) => {
          console.log('[Dashboard] 笔记选择回调触发，同步更新 selectedNote:', note?.id);
          // 同步更新 Dashboard 的 selectedNote 状态
          setSelectedNote(note);
          setIsUserSelectedNote(true);
        }
      });
      console.log('[Dashboard] 回调函数设置完成');
    } else {
      console.log('[Dashboard] setNoteOperationCallback 还未准备好');
    }
  }, [setNoteOperationCallback, fetchNotesStats, setSelectedNote, setIsUserSelectedNote]); // 添加 setSelectedNote 和 setIsUserSelectedNote 依赖

  // 无限滚动监听器
  useEffect(() => {
    const noteListElement = noteListRef.current;
    if (!noteListElement) {
      return;
    }

    const handleScroll = () => {
      // 搜索时不进行无限滚动
      if (searchKeyword && searchKeyword.trim()) {
        return;
      }

      const { scrollTop, scrollHeight, clientHeight } = noteListElement;
      const isNearBottom = scrollTop + clientHeight >= scrollHeight - 50; // 距离底部50px时开始加载

      if (isNearBottom && !isLoading) {
        const totalPages = Math.ceil(pagination.total / pagination.limit);
        if (pagination.page < totalPages) {
          // 传递当前的标签和主题筛选条件
          const currentTags = selectedTag ? [selectedTag.name] : [];
          const currentTopicId = selectedTopic ? selectedTopic.topic_id : null;
          const currentKbId = selectedKB ? selectedKB.kb_id : null;
          const filterType = selectedFolder === 'private' ? 'private' :
            selectedFolder === 'collaborative' ? 'collaborative' :
              selectedFolder === 'pinned' ? 'pinned' :
                selectedFolder === 'recent' ? 'recent' : 'all';
          loadMoreNotes(currentTags, currentTopicId, false, filterType, currentKbId);
        }
      }
    };

    noteListElement.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      noteListElement.removeEventListener('scroll', handleScroll);
    };
  }, [loadMoreNotes, isLoading, pagination, searchKeyword, selectedTag, selectedTopic, selectedKB, selectedFolder]);

  // 组件卸载时清理计时器
  useEffect(() => {
    return () => {
      if (timerInterval) {
        clearInterval(timerInterval);
      }
    };
  }, [timerInterval]);

  // 已修复无限循环问题：
  // 1. 添加了防抖机制，避免 ResizeObserver 频繁触发
  // 2. 添加了宽度变化检查，只在真正需要时更新状态
  // 3. 使用函数式更新器避免不必要的重新渲染
  useEffect(() => {
    const containerForResizablePanes = resizablePanesContainerRef.current;
    if (!containerForResizablePanes) return;

    let resizeTimeout;
    let lastWidth = 0;

    const updateLayoutConstraints = () => {
      const availableWidthForResizablePanes = containerForResizablePanes.offsetWidth;

      // 只有当宽度真正改变时才更新
      if (availableWidthForResizablePanes > 0 && availableWidthForResizablePanes !== lastWidth) {
        lastWidth = availableWidthForResizablePanes;

        // Max width for note list is the available width minus the preview's min width
        const newMax = availableWidthForResizablePanes - MIN_PREVIEW_WIDTH;
        // Ensure this max is not less than the note list's own min width
        const finalNewMax = Math.max(newMax, MIN_NOTE_LIST_WIDTH);

        // 只有当最大宽度真正改变时才更新状态
        setMaxNoteListSiderWidth(prevMax => {
          if (prevMax !== finalNewMax) {
            return finalNewMax;
          }
          return prevMax;
        });

        // Adjust current noteListWidth if it's outside the new bounds
        setNoteListWidth(currentW => {
          // If noteListWidth is null (initial state), calculate adaptive widths based on screen size
          if (currentW === null) {
            const adaptiveWidths = getAdaptiveInitialWidths(availableWidthForResizablePanes);
            const initialNoteListWidth = adaptiveWidths.noteListWidth;
            return Math.max(Math.min(initialNoteListWidth, finalNewMax), MIN_NOTE_LIST_WIDTH);
          }
          // Otherwise, adjust if outside bounds
          let adjusted = Math.max(currentW, MIN_NOTE_LIST_WIDTH);
          adjusted = Math.min(adjusted, finalNewMax);
          return adjusted;
        });
      }
    };

    const debouncedUpdateLayoutConstraints = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(updateLayoutConstraints, 16); // 约60fps的防抖
    };

    const resizeObserver = new ResizeObserver(entries => {
      if (entries[0]) {
        debouncedUpdateLayoutConstraints();
      }
    });

    resizeObserver.observe(containerForResizablePanes);
    updateLayoutConstraints(); // Initial call to set constraints

    return () => {
      clearTimeout(resizeTimeout);
      resizeObserver.disconnect();
    };
  }, [getAdaptiveInitialWidths]); // Include getAdaptiveInitialWidths in dependencies

  // 处理拖拽调整分割线
  const handleMouseDown = (e) => {
    setIsDragging(true);
    e.preventDefault();
  };

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e) => {
      const container = resizablePanesContainerRef.current;
      if (!container) return;

      const containerRect = container.getBoundingClientRect();
      const availableWidth = containerRect.width;
      const newWidth = e.clientX - containerRect.left;

      // 计算右侧面板宽度
      const previewWidth = availableWidth - newWidth;

      // 确保右侧面板不小于最小宽度
      const minPreviewWidth = MIN_PREVIEW_WIDTH;
      const maxNoteListWidth = availableWidth - minPreviewWidth;

      // 应用最小和最大宽度限制（同时考虑左侧和右侧的最小宽度）
      const constrainedWidth = Math.max(
        MIN_NOTE_LIST_WIDTH,
        Math.min(newWidth, Math.min(maxNoteListSiderWidth, maxNoteListWidth))
      );

      setNoteListWidth(constrainedWidth);
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, maxNoteListSiderWidth, setNoteListWidth]);
  // 用于跟踪快捷操作后是否需要刷新 notesStats
  const shouldRefreshStatsAfterQuickAction = useRef(false);
  const lastQuickActionFolder = useRef(null);

  // 处理快捷操作点击
  const handleQuickActionClick = useCallback((action) => {
    console.log('[Dashboard] 快捷操作点击:', action);

    // 检查是否与当前选中的文件夹相同，避免重复更新
    if (selectedFolder === action.key) {
      console.log('[Dashboard] 当前已选中该快捷操作，跳过重复更新:', action.key);
      return;
    }

    // 标记需要刷新 notesStats
    const quickActionKeys = ['all', 'private', 'collaborative', 'pinned', 'recent'];
    if (quickActionKeys.includes(action.key)) {
      console.log('[Dashboard] 标记需要刷新 notesStats，快捷操作:', action.key);
      shouldRefreshStatsAfterQuickAction.current = true;
      lastQuickActionFolder.current = action.key;
    }

    updateGlobalState({
      selectedFolder: action.key,
      selectedKB: null,
      selectedTopic: null,
      selectedTag: null,
      searchMode: 'hybrid',   // 这里强制设置为缺省的混合检索模式。 WLAN 08.21
      // 移除 searchKeyword: ''，保持用户搜索状态
      // searchKeyword: ''
    }, 'Dashboard-handleQuickActionClick');

    console.log('[Dashboard] 快捷操作状态已更新');
  }, [selectedFolder]); // 添加selectedFolder依赖

  // 使用工具函数生成快捷操作数据（基于统计数据而不是当前笔记列表）
  const quickActionsData = useMemo(() => getQuickActions(notesStats), [notesStats]);
  const quickActions = useMemo(() => {
    console.log('[Dashboard] 生成快捷操作:', { quickActionsData, notesStats });

    return quickActionsData.map(action => {
      // 添加翻译标题
      let translatedTitle = action.title;
      switch (action.key) {
        case 'all':
          translatedTitle = t('dashboard:sidebar.all_notes');
          break;
        case 'pinned':
          translatedTitle = t('dashboard:sidebar.favorites');
          break;
        case 'recent':
          translatedTitle = t('dashboard:sidebar.recent');
          break;
      }

      const quickAction = {
        ...action,
        title: translatedTitle,
        icon: action.icon === 'FolderOutlined' ? <FolderOutlined /> :
          action.icon === 'StarOutlined' ? <StarOutlined /> :
            action.icon === 'ClockCircleOutlined' ? <ClockCircleOutlined /> : null,
        onClick: () => {
          console.log('[Dashboard] 快捷操作 onClick 触发:', action.key);
          handleQuickActionClick(action);
        }
      };

      console.log('[Dashboard] 生成的快捷操作:', quickAction);
      return quickAction;
    });
  }, [quickActionsData, t, handleQuickActionClick]);
  // 当搜索关键词或文件夹改变时，重置笔记列表
  useEffect(() => {
    // 添加全局状态监听器
    const removeListener = addGlobalStateListener((newState, oldState, updates) => {
      // 检查是否有相关的状态变化
      if (updates.selectedFolder || updates.selectedTag || updates.selectedTopic || updates.selectedKB || updates.searchKeyword) {
        console.log('[前端] 全局状态变化，重新加载笔记数据:', {
          updates,
          oldState: {
            selectedFolder: oldState.selectedFolder,
            selectedTag: oldState.selectedTag,
            selectedTopic: oldState.selectedTopic,
            selectedKB: oldState.selectedKB,
            searchKeyword: oldState.searchKeyword
          },
          newState: {
            selectedFolder: newState.selectedFolder,
            selectedTag: newState.selectedTag,
            selectedTopic: newState.selectedTopic,
            selectedKB: newState.selectedKB,
            searchKeyword: newState.searchKeyword
          }
        });

        // 注意：数据获取现在由 NoteListContext 自动处理
        // 这里只处理 UI 相关的逻辑，如滚动位置重置

        // 切换文件夹或标签后，滚动到笔记列表顶部
        // 只有在非用户主动选择笔记的情况下才重置滚动位置
        if (!newState.isUserSelectedNote && noteListRef.current) {
          setTimeout(() => {
            if (noteListRef.current) {
              noteListRef.current.scrollTop = 0;
            }
          }, 200);
        }
      }
    });

    // 清理监听器
    return removeListener;
  }, [noteListRef]); // 移除 fetchNotes 依赖，因为数据获取由 NoteListContext 处理

  useEffect(() => {
    const currentIsUserSelectedNote = getIsUserSelectedNote();
    const currentSelectedNote = getSelectedNote();
    const currentPagination = getPagination();

    console.log('[前端] displayNotes useEffect 触发:', {
      displayNotesLength: displayNotes.length,
      isUserSelectedNote: currentIsUserSelectedNote,
      selectedNoteId: currentSelectedNote?.id,
      displayNotesFirstId: displayNotes[0]?.id
    });

    // 如果是快捷操作后的 fetchNotes 完成，刷新 notesStats
    if (shouldRefreshStatsAfterQuickAction.current) {
      const currentFolder = getSelectedFolder();
      const quickActionKeys = ['all', 'private', 'collaborative', 'pinned', 'recent'];

      // 确认当前选中的是快捷操作，且与标记的快捷操作一致
      if (quickActionKeys.includes(currentFolder) && currentFolder === lastQuickActionFolder.current) {
        console.log('[Dashboard-displayNotes-useEffect-shouldRefreshStatsAfterQuickAction] fetchNotes 完成，开始刷新 notesStats...');
        shouldRefreshStatsAfterQuickAction.current = false;
        lastQuickActionFolder.current = null;

        // 刷新 notesStats
        fetchNotesStats().then(() => {
          console.log('[Dashboard-displayNotes-useEffect-shouldRefreshStatsAfterQuickAction] 快捷操作后，notesStats 刷新完成');
        }).catch(error => {
          console.error('[Dashboard-displayNotes-useEffect-shouldRefreshStatsAfterQuickAction] 刷新 notesStats 失败:', error);
        });
      }
    }

    // 检查是否为滚动加载（分页大于1且有选中笔记）
    if (currentPagination.page > 1 && currentSelectedNote) {
      console.log('[前端] 检测到滚动加载状态，跳过自动选择笔记，保持当前选中状态');
      return;
    }

    if (displayNotes.length > 0) {
      // 只有在用户没有手动选择笔记时，才自动选择第一条笔记
      if (!currentIsUserSelectedNote) {
        console.log('[前端] 自动选择第一条笔记:', displayNotes[0]?.id);
        setSelectedNote(displayNotes[0]);
      } else {
        console.log('[前端] 用户已手动选择笔记，跳过自动选择');
      }

      // 滚动到笔记列表顶部
      // 只有在非用户主动选择笔记的情况下才重置滚动位置
      if (!currentIsUserSelectedNote && noteListRef.current) {
        noteListRef.current.scrollTop = 0;
      }
    } else if (displayNotes.length === 0) {
      console.log('[前端] displayNotes 为空，清空选中笔记');
      setSelectedNote(null);
      // 只有在没有用户手动选择的笔记时，才重置用户选择标志
      if (!currentIsUserSelectedNote) {
        setIsUserSelectedNote(false);
      }
    }
  }, [displayNotes, fetchNotesStats, getSelectedFolder]); // 添加 fetchNotesStats 和 getSelectedFolder 依赖

  // 新增：监听分页变化，避免滚动加载时自动选择笔记
  useEffect(() => {
    // 如果当前有选中的笔记，且分页发生变化（说明是滚动加载），不进行自动选择
    const currentSelectedNote = getSelectedNote();
    const currentPagination = getPagination();

    if (currentSelectedNote && currentPagination.page > 1) {
      console.log('[Dashboard] 检测到分页变化，跳过自动选择笔记，保持当前选中状态');
      return;
    }
  }, [getSelectedNote, getPagination]); // 添加必要的依赖

  // 当搜索关键词变化时，自动滚动到顶部并选择第一条笔记
  useEffect(() => {
    const currentSearchKeyword = getSearchKeyword();
    const currentIsUserSelectedNote = getIsUserSelectedNote();

    console.log('[前端] 搜索关键词 useEffect 触发:', {
      searchKeyword: currentSearchKeyword,
      searchResultsLength: searchResults.length,
      isUserSelectedNote: currentIsUserSelectedNote
    });

    if (currentSearchKeyword && currentSearchKeyword.trim() && searchResults.length > 0) {
      // 只有在用户没有手动选择笔记时，才自动选择第一条搜索结果
      if (!currentIsUserSelectedNote) {
        console.log('[前端] 自动选择第一条搜索结果:', searchResults[0]?.id);
        setSelectedNote(searchResults[0]);
      } else {
        console.log('[前端] 用户已手动选择笔记，跳过自动选择搜索结果');
      }

      // 滚动到笔记列表顶部
      // 只有在非用户主动选择笔记的情况下才重置滚动位置
      if (!currentIsUserSelectedNote && noteListRef.current) {
        noteListRef.current.scrollTop = 0;
      }
    }
  }, [searchResults, getSearchKeyword, getIsUserSelectedNote, setSelectedNote, noteListRef]); // 添加必要的依赖

  // 当选择的笔记改变时，获取主题层级信息并激活笔记内容标签页
  useEffect(() => {
    if (selectedNote) {
      // 自动显示详情面板（如果当前是隐藏状态）
      //if (!isRightPanelVisible) {
      //  console.log('[Dashboard] 检测到笔记被选中，自动显示详情面板');
      //  setIsRightPanelVisible(true);
      //}

      // 激活笔记内容标签页
      setActiveTabKey('content');

      // 检查是否与上次获取的笔记ID相同，避免重复调用
      if (lastFetchedNoteId !== selectedNote.id) {
        console.log('[前端] 笔记ID发生变化，获取主题层级信息:', {
          lastFetchedNoteId,
          currentNoteId: selectedNote.id,
          noteTitle: selectedNote.title
        });
        setLastFetchedNoteId(selectedNote.id);
        fetchNoteTopicHierarchy(selectedNote.id);
      } else {
        console.log('[前端] 笔记ID未变化，跳过主题层级信息获取:', selectedNote.id);
      }
    } else {
      setNoteTopicHierarchy(null);
      setLastFetchedNoteId(null);
    }
  }, [selectedNote, lastFetchedNoteId, isRightPanelVisible, setActiveTabKey, setLastFetchedNoteId, setNoteTopicHierarchy, fetchNoteTopicHierarchy]);

  useEffect(() => {
    if (!selectedNote) {
      setRealContent('');
      setIsLoadingContent(false);
      return;
    }

    // 使用统一的 loadNoteContent 函数加载笔记内容
    setIsLoadingContent(true);
    loadNoteContent(selectedNote, apiClient, t, isFilePath)
      .then(result => {
        setRealContent(result.content);
        setIsLoadingContent(result.needsLoading);
      })
      .catch(error => {
        console.error('[Dashboard] 加载笔记内容失败:', error);
        setRealContent(t('dashboard:no_content', { defaultValue: 'No content' }));
        setIsLoadingContent(false);
      });
  }, [selectedNote, setRealContent, setIsLoadingContent, t]);

  // 当选中笔记改变时，重置内容展开状态
  useEffect(() => {
    setIsContentExpanded(false);
  }, [selectedNote, setIsContentExpanded]);

  // 当 qaInitialQuestion 被设置后，延迟清除它（确保 NoteListAsk 已经处理完）
  useEffect(() => {
    if (qaInitialQuestion) {
      const timer = setTimeout(() => {
        setQaInitialQuestion(null);
      }, 500); // 延迟 500ms 清除，确保 NoteListAsk 已经处理完
      return () => clearTimeout(timer);
    }
  }, [qaInitialQuestion]);

  const stopCreateTimer = () => {
    if (timerInterval) {
      clearInterval(timerInterval);
      setTimerInterval(null);
    }
    setIsCreatingNote(false);
    setCreateTimer(0);
  };

  // 标签页相关处理函数
  const handleTabChange = (key) => {
    setActiveTabKey(key);
  };

  // 处理从问答页面点击笔记
  const handleNoteClick = useCallback(async (noteId) => {
    try {
      // 检查新note_id和当前显示的note_id是否一致
      if (selectedNote && selectedNote.id === parseInt(noteId)) {
        console.log('[handleNoteClick] 当前笔记已选中，跳过API调用');
        // 如果一致，只切换到笔记内容标签页，不调用后台
        setActiveTabKey('content');
        return;
      }

      // 如果不一致或者当前无笔记显示，调用后台获取数据
      console.log('[handleNoteClick] 调用后台获取笔记详情，noteId:', noteId);
      const response = await apiClient.get(`/notes/${noteId}`);
      console.log('[handleNoteClick] API Response:', response.data);

      // 检查响应数据结构
      if (response.data && response.data.id) {
        // 设置用户选择标志，防止被自动选择逻辑覆盖
        setIsUserSelectedNote(true);
        // 直接使用返回的笔记数据
        setSelectedNote(response.data);
        // 切换到笔记内容标签页
        setActiveTabKey('content');
        message.success(t('messages:switched_to_note_content', { defaultValue: 'Switched to note content' }));
      } else if (response.data && response.data.success && response.data.note) {
        // 设置用户选择标志，防止被自动选择逻辑覆盖
        setIsUserSelectedNote(true);
        // 使用嵌套的note字段
        setSelectedNote(response.data.note);
        setActiveTabKey('content');
        message.success(t('messages:switched_to_note_content', { defaultValue: 'Switched to note content' }));
      } else {
        console.error('[handleNoteClick] Unexpected response structure:', response.data);
        message.error(t('messages:error_loading', { defaultValue: 'Error loading data' }));
      }
    } catch (error) {
      console.error('获取笔记失败:', error);
      message.error(t('messages:error_loading', { defaultValue: 'Error loading data' }));
    }
  }, [selectedNote, setActiveTabKey, setIsUserSelectedNote, setSelectedNote]);

  // 新增：专门处理从 DashboardQA.js 点击主题的函数
  const handleQATopicClick = useCallback(async (noteId) => {
    try {
      console.log('[handleQATopicClick] 从QA页面点击主题，获取笔记详情，noteId:', noteId);
      console.log('[handleQATopicClick] noteId type:', typeof noteId);
      console.log('[handleQATopicClick] noteId value:', noteId);

      // 使用新的优化端点，一次性获取笔记详情和主题信息（使用缓存服务）
      const response = await apiClient.get(`/notes/${noteId}/with-topics`);
      console.log('[handleQATopicClick] API Response:', response.data);

      // 检查响应数据结构
      if (response.data && response.data.note && response.data.topics) {
        console.log('[handleQATopicClick] 设置用户选择标志和笔记数据:', {
          noteId: response.data.note.id,
          noteTitle: response.data.note.title,
          topicsCount: response.data.topics.length
        });

        // 设置用户选择标志，防止被自动选择逻辑覆盖
        setIsUserSelectedNote(true);
        // 直接使用返回的笔记数据
        setSelectedNote(response.data.note);
        // 直接设置主题层级信息，避免触发useEffect
        setNoteTopicHierarchy(response.data.topics);
        setLastFetchedNoteId(noteId);
        // 切换到笔记内容标签页
        setActiveTabKey('content');
        message.success(t('messages:switched_to_note_content', { defaultValue: 'Switched to note content' }));
      } else {
        console.error('[handleQATopicClick] Unexpected response structure:', response.data);
        message.error(t('messages:error_loading', { defaultValue: 'Error loading data' }));
      }
    } catch (error) {
      console.error('[handleQATopicClick] 获取笔记失败:', error);
      if (error.response && error.response.status === 404) {
        message.error(t('messages:note_not_found', { defaultValue: 'Note not found' }));
      } else {
        message.error(t('messages:error_loading', { defaultValue: 'Error loading data' }));
      }
    }
  }, [setIsUserSelectedNote, setSelectedNote, setNoteTopicHierarchy, setLastFetchedNoteId, setActiveTabKey]);

  // 包装创建笔记函数，添加笔记库ID传递和当前筛选状态
  const wrappedHandleCreateNote = async (values) => {
    console.log('[Dashboard] ===== wrappedHandleCreateNote 开始 =====');


    setIsBatchCreating(true); // 统一loading状态

    // 显示AI分析进度弹窗
    setIsCreatingNote(true);
    setCreateTimer(0);

    // 启动计时器
    const timer = setInterval(() => {
      setCreateTimer(prev => prev + 1);
    }, 1000);

    // 添加笔记库ID到创建数据中，并确保手动输入的内容被包含
    const createData = {
      ...values,
      content: manualInputContent || values.content, // 优先使用手动输入的内容
      kb_id: createNoteKnowledgeBase?.kb_id || null
    };

    console.log('[Dashboard] 完整创建数据:', createData);

    try {
      console.log('[Dashboard] 发送 API 请求到 /notes');
      // 直接调用API创建笔记，避免使用originalHandlers.handleCreateNote
      const response = await apiClient.post('/notes', {
        title: createData.title,
        content: createData.content,
        source_url: createData.source_url,
        source_type: 'md',
        kb_id: createData.kb_id,
      });

      console.log('[Dashboard] API 响应:', response);

      await refreshKnowledgeBasesAndTopics();

      // 关闭AI分析进度弹窗
      setIsCreatingNote(false);
      clearInterval(timer);

      // 根据笔记的知识库类型存储sidebar选项信息（不立即设置，因为马上要刷新页面）
      const createdNote = response.data;
      if (createdNote && createdNote.kb_id) {
        // 检查是否是协作库
        const isCollaborativeKB = collaborativeKBs && collaborativeKBs.some(kb => kb.kb_id === createdNote.kb_id);
        
        if (isCollaborativeKB) {
          // 如果是协作库，找到对应的KB对象并存储
          const targetKB = collaborativeKBs.find(kb => kb.kb_id === createdNote.kb_id);
          if (targetKB) {
            console.log('[Dashboard] 创建笔记成功，存储协作库信息:', targetKB.name);
            // 只存储到localStorage，刷新后设置（避免立即设置导致状态冲突）
            localStorage.setItem('createNoteSelectedKB', JSON.stringify({ kb_id: targetKB.kb_id, name: targetKB.name }));
          }
        } else {
          // 如果是私有库，存储私有库信息
          console.log('[Dashboard] 创建笔记成功，存储私有库信息');
          // 只存储到localStorage，刷新后设置（避免立即设置导致状态冲突）
          localStorage.setItem('createNoteSelectedFolder', 'private');
        }
      }
      
      // 存储新建笔记的ID，刷新后自动选中
      if (createdNote && createdNote.id) {
        console.log('[Dashboard] 存储新建笔记ID，刷新后自动选中:', createdNote.id);
        localStorage.setItem('createNoteId', createdNote.id.toString());
      }

      // 统一结果显示
      setBatchResult({
        successCount: 1,
        failedCount: 0,
        totalCount: 1,
        failedFiles: [],
        successFiles: [{ fileName: createData.title || t('notes:untitled', { defaultValue: 'Untitled' }) }]
      });

      setTimeout(() => {
        setIsBatchCreating(false);
        setBatchResult(null);
        handleCreateModalCancel();
        // 设置标志，表示新建笔记后需要重置到指定的知识库
        localStorage.setItem('resetToAllNotesAfterCreate', 'true');
        // 创建单一笔记成功，刷新页面
        window.location.reload();
      }, 1500);
    } catch (error) {
      // 关闭AI分析进度弹窗
      setIsCreatingNote(false);
      clearInterval(timer);

      setBatchResult({
        successCount: 0,
        failedCount: 1,
        totalCount: 1,
        failedFiles: [{ fileName: createData.title || t('notes:untitled', { defaultValue: 'Untitled' }), error: error.message }],
        successFiles: []
      });
      setTimeout(() => {
        setIsBatchCreating(false);
        setBatchResult(null);
        handleCreateModalCancel();
        // 创建单一笔记失败，不刷新页面
        // 不设置 resetToAllNotesAfterCreate 标志
        // 不调用 window.location.reload()
      }, 1500);
    }
  };

  const wrappedHandleCreateFromLink = async (values) => {
    let sourceUrl = values.url || '';
    let sourceType = 'web'; // 默认为web类型
    let fileUploadResult = null;

    // 首先判断是文件上传还是链接模式
    if (selectedFiles && selectedFiles.length > 0) {
      setCreateMode('link'); // 文件上传模式
    } else if (sourceUrl && sourceUrl.trim().length > 0) {
      setCreateMode('link'); // 链接模式
    } else {
      setCreateMode('link'); // 统一设置为link模式，因为这是链接/文件标签页
    }
    // 验证至少有文件选择（链接地址字段已隐藏，主要依靠文件上传）
    if (!selectedFiles || selectedFiles.length === 0) {
      message.error(t('messages:please_select_target', { defaultValue: 'Please select target' }));
      return;
    }

    // 验证文件类型（允许的扩展名列表）- 作为双重保险
    const allowedExtensions = ['.txt', '.md', '.pdf', '.pptx', '.xlsx', '.docx'];
    const invalidFiles = selectedFiles.filter(file => {
      const fileName = file.name.toLowerCase();
      const fileExtension = fileName.substring(fileName.lastIndexOf('.'));
      return !allowedExtensions.includes(fileExtension);
    });

    if (invalidFiles.length > 0) {
      const invalidFileNames = invalidFiles.map(file => file.name).join(', ');
      const errorMsg = t('messages:file_type_not_supported', {
        defaultValue: 'File type not supported: {{fileName}}. Allowed types: {{types}}',
        fileName: invalidFileNames,
        types: allowedExtensions.join(', ')
      });
      message.error(errorMsg);
      return;
    }

    // 验证文件大小（15MB限制）- 作为双重保险
    const maxFileSize = 15 * 1024 * 1024; // 15MB
    const oversizedFiles = selectedFiles.filter(file => file.size > maxFileSize);

    if (oversizedFiles.length > 0) {
      const oversizedFileNames = oversizedFiles.map(file => file.name).join(', ');
      const errorMsg = t('messages:file_too_large', {
        error: `${oversizedFileNames}: ${t('messages:file_size_exceeds_limit')}`
      });
      alert(errorMsg);
      return;
    }

    if (selectedFiles && selectedFiles.length > 0) {
      try {
        console.log('[Dashboard] 开始上传文件：', selectedFiles);
        fileUploadResult = await fileService.uploadMultipleFiles(selectedFiles, 'document');
        console.log('[Dashboard] 上传返回原始结果：', fileUploadResult);

        // 检查返回格式并解析URLs
        let urls = [];

        console.log('[Dashboard] 检查返回结果结构：', {
          hasSuccess: !!fileUploadResult.success,
          successIsArray: Array.isArray(fileUploadResult.success),
          successLength: fileUploadResult.success ? fileUploadResult.success.length : 0,
          hasFailed: !!fileUploadResult.failed,
          failedLength: fileUploadResult.failed ? fileUploadResult.failed.length : 0,
          firstSuccessItem: fileUploadResult.success ? fileUploadResult.success[0] : null
        });

        // 检查是否有失败的文件
        if (fileUploadResult.failed && fileUploadResult.failed.length > 0) {
          console.warn('[Dashboard] 部分文件上传失败：', fileUploadResult.failed);
          fileUploadResult.failed.forEach(failedFile => {
            message.warning(t('messages:upload_failed', { error: `${failedFile.fileName}: ${failedFile.error}` }));
          });
        }

        // 解析成功上传的文件URLs
        if (fileUploadResult.success && Array.isArray(fileUploadResult.success) && fileUploadResult.success.length > 0) {
          console.log('[Dashboard] 使用success数组解析URLs');
          urls = fileUploadResult.success.map(item => {
            console.log('[Dashboard] 处理success项：', item);
            if (item && item.url) {
              console.log('[Dashboard] 提取的URL：', item.url);
              return item.url;
            } else {
              console.warn('[Dashboard] success项缺少URL字段：', item);
              return null;
            }
          }).filter(Boolean);
        } else if (fileUploadResult.data && Array.isArray(fileUploadResult.data)) {
          console.log('[Dashboard] 使用data数组解析URLs');
          urls = fileUploadResult.data.map(item => item.url).filter(Boolean);
        } else if (fileUploadResult.url) {
          console.log('[Dashboard] 使用直接url字段');
          urls = [fileUploadResult.url];
        } else {
          console.error('[Dashboard] 无法解析返回结果，检查完整数据结构：', JSON.stringify(fileUploadResult, null, 2));
        }

        console.log('[Dashboard] 解析得到的URLs：', urls);

        if (urls.length > 0) {
          setUploadedFileUrls(urls);
          console.log('[Dashboard] 上传成功，获得URLs：', urls);

          // 设置批量创建状态
          setIsBatchCreating(true);

          // 显示AI分析进度弹窗
          setIsCreatingNote(true);
          setCreateTimer(0);

          // 启动计时器
          const timer = setInterval(() => {
            setCreateTimer(prev => prev + 1);
          }, 1000);

          try {
            // 为每个文件创建一条笔记
            const createPromises = urls.map(async (url, index) => {
              const file = selectedFiles[index];
              const fileName = file.name.toLowerCase();

              // 根据文件类型设置source_type
              let fileSourceType = 'other';
              if (fileName.endsWith('.pdf')) {
                fileSourceType = 'pdf';
              } else if (fileName.endsWith('.docx')) {
                fileSourceType = 'docx';
              } else if (fileName.endsWith('.pptx')) {
                fileSourceType = 'pptx';
              } else if (fileName.endsWith('.xlsx')) {
                fileSourceType = 'xlsx';
              } else if (fileName.endsWith('.txt')) {
                fileSourceType = 'txt';
              } else if (fileName.endsWith('.md')) {
                fileSourceType = 'md';
              } else if (fileName.match(/\.(jpg|jpeg|png|gif|bmp|webp)$/)) {
                fileSourceType = 'image';
              }

              // 构造创建笔记的数据
              const createData = {
                title: fileName, // 后端会自动生成标题
                content: '', // 提供空的content字段，后端会从文件URL提取内容
                source_url: url,
                source_type: fileSourceType,
                kb_id: createNoteKnowledgeBase?.kb_id || null
              };

              console.log(`[Dashboard] 为文件 ${file.name} 创建笔记：`, createData);

              try {
                // 调用创建笔记API
                const response = await apiClient.post('/notes', createData);
                console.log(`[Dashboard] 文件 ${file.name} 笔记创建成功：`, response.data);
                return { success: true, fileName: file.name, note: response.data };
              } catch (error) {
                console.error(`[Dashboard] 文件 ${file.name} 笔记创建失败：`, error);
                return { success: false, fileName: file.name, error: error.message };
              }
            });

            // 等待所有笔记创建完成
            const results = await Promise.all(createPromises);

            // 统计成功和失败的数量
            const successCount = results.filter(r => r.success).length;
            const failedCount = results.filter(r => !r.success).length;

            // 设置批量结果
            const result = {
              successCount,
              failedCount,
              totalCount: urls.length,
              failedFiles: results.filter(r => !r.success),
              successFiles: results.filter(r => r.success)
            };

            setBatchResult(result);

            // 显示结果消息
            if (successCount > 0) {
              message.success(t('messages:create_note_success', { defaultValue: 'Note created successfully!' }));
            } else {
              message.error(t('messages:operation_failed', { defaultValue: 'Operation failed' }));
            }

            // 显示失败的文件信息
            const failedFiles = results.filter(r => !r.success);
            if (failedFiles.length > 0) {
              failedFiles.forEach(failed => {
                message.warning(t('messages:upload_failed', { error: `${failed.fileName}: ${failed.error}` }));
              });
            }

            // 刷新笔记列表
            await refreshKnowledgeBasesAndTopics();

            // 关闭AI分析进度弹窗
            setIsCreatingNote(false);
            clearInterval(timer);

            // 根据实际状态决定是否刷新页面
            const shouldReload = successCount > 0; // 如果有成功的，刷新页面

            // 如果有成功的笔记，根据第一个成功笔记的知识库类型存储sidebar选项信息（不立即设置，因为马上要刷新页面）
            const successFiles = results.filter(r => r.success);
            if (shouldReload && successFiles.length > 0) {
              const firstSuccessNote = successFiles[0].note;
              if (firstSuccessNote && firstSuccessNote.kb_id) {
                // 检查是否是协作库
                const isCollaborativeKB = collaborativeKBs && collaborativeKBs.some(kb => kb.kb_id === firstSuccessNote.kb_id);
                
                if (isCollaborativeKB) {
                  // 如果是协作库，找到对应的KB对象并存储
                  const targetKB = collaborativeKBs.find(kb => kb.kb_id === firstSuccessNote.kb_id);
                  if (targetKB) {
                    console.log('[Dashboard] 批量创建笔记成功，存储协作库信息:', targetKB.name);
                    // 只存储到localStorage，刷新后设置（避免立即设置导致状态冲突）
                    localStorage.setItem('createNoteSelectedKB', JSON.stringify({ kb_id: targetKB.kb_id, name: targetKB.name }));
                  }
                } else {
                  // 如果是私有库，存储私有库信息
                  console.log('[Dashboard] 批量创建笔记成功，存储私有库信息');
                  // 只存储到localStorage，刷新后设置（避免立即设置导致状态冲突）
                  localStorage.setItem('createNoteSelectedFolder', 'private');
                }
              }
              
              // 存储第一个成功笔记的ID，刷新后自动选中
              if (firstSuccessNote && firstSuccessNote.id) {
                console.log('[Dashboard] 批量创建笔记成功，存储第一个成功笔记ID，刷新后自动选中:', firstSuccessNote.id);
                localStorage.setItem('createNoteId', firstSuccessNote.id.toString());
              }
            }

            setTimeout(() => {
              setIsBatchCreating(false);
              setBatchResult(null);
              handleCreateModalCancel();
              
              if (shouldReload) {
                // 批量创建笔记如果有成功的，刷新页面
                localStorage.setItem('resetToAllNotesAfterCreate', 'true');
                window.location.reload();
              } else {
                // 批量创建笔记都失败，不刷新页面
                // 不设置 resetToAllNotesAfterCreate 标志
                // 不调用 window.location.reload()
              }
            }, 1500);

          } catch (error) {
            console.error('[Dashboard] 批量创建笔记失败：', error);
            message.error(t('messages:operation_failed', { defaultValue: 'Operation failed' }) + '：' + error.message);
            setIsBatchCreating(false);
            setIsCreatingNote(false);
            clearInterval(timer);
          }

        } else {
          console.error('[Dashboard] 文件上传失败，未获得有效URL，完整返回：', fileUploadResult);

          // 检查是否全部文件都上传失败
          if (fileUploadResult.failed && fileUploadResult.failed.length > 0) {
            const failedFileNames = fileUploadResult.failed.map(f => f.fileName).join(', ');
            const failedErrors = fileUploadResult.failed.map(f => f.error).join(', ');
            message.error(t('messages:upload_failed', { error: `${failedFileNames}: ${failedErrors}` }));
          } else if (!fileUploadResult.success || fileUploadResult.success.length === 0) {
            message.error(t('messages:upload_failed', { error: t('messages:unknown_error', { defaultValue: 'Unknown error' }) }));
          } else {
            message.error(t('messages:upload_failed', { error: t('messages:unknown_error', { defaultValue: 'Unknown error' }) }));
          }
          return;
        }
      } catch (err) {
        console.error('[Dashboard] 文件上传异常：', err);
        message.error(t('messages:upload_failed', { error: (err.message || t('messages:unknown_error')) }));
        return;
      }
    }
  };

  // 创建处理函数
  const handlers = createDashboardHandlers({
    navigate,
    logout,
    user,
    selectedNote: selectedNote, // 使用本地状态而不是全局状态
    setSelectedNote,
    isEditing,
    setIsEditing,
    editTitle,
    setEditTitle,
    editSummary,
    setEditSummary,
    editContent,
    setEditContent,
    fetchNotes,
    updateNote,
    deleteNote,
    togglePinNote,
    displayNotes,
    isAddingTag,
    setIsAddingTag,
    newTagName,
    setNewTagName,
    userHistoryTags,
    setUserHistoryTags,
    isLoadingHistoryTags,
    setIsLoadingHistoryTags,
    fetchUserCustomTags,
    selectedTag: getSelectedTag(),
    setSelectedTag,
    setTimerInterval,
    timerInterval,
    setIsCreatingNote,
    setCreateTimer,
    setIsCreateModalVisible,
    createForm,
    linkForm,
    setCreateMode,
    stopCreateTimer,
    setIsLoadingHistoryTags,
    refreshKnowledgeBasesAndTopics,
    fetchNotesStats,
    // 添加当前筛选状态
    currentFilterState: {
      selectedFolder: getSelectedFolder(),
      selectedTag: getSelectedTag(),
      selectedTopic: getSelectedTopic(),
      selectedKB: getSelectedKB()
    },
    // 新增：传递realContent用于编辑内容同步
    realContent
  });

  const {
    handleLogout,
    handleEdit,
    handleSave,
    handlePin,
    handleTogglePublic,
    handleCancel,
    startCreateTimer,
    handleCreateModalCancel: originalHandleCreateModalCancel,
    handleFileUpload,
    handleFetchUserHistoryTags,
    handleQuickAddTag,
    handleAddTag,
    handleAddCustomTag,
    handleTagClick,
    handleDeleteCustomTag,
    handleUseTopicTemplate
  } = handlers;

  // 刷新笔记内容
  const handleRefreshNote = useCallback(async () => {
    if (!selectedNote?.id) {
      message.warning(t('messages:please_select_note', { defaultValue: 'Please select a note first' }));
      return;
    }

    try {
      message.loading('Refreshing note content...', 1);

      // 获取最新的笔记详情
      const response = await apiClient.get(`/notes/${selectedNote.id}`);

      console.log('[Dashboard] 刷新笔记API响应:', response.data);

      let updatedNote = null;

      // 检查不同的响应数据结构
      if (response.data && response.data.success && response.data.data) {
        // 标准格式：{success: true, data: {...}}
        updatedNote = response.data.data;
      } else if (response.data && response.data.id) {
        // 直接返回笔记数据：{id: ..., title: ..., ...}
        updatedNote = response.data;
      } else if (response.data && response.data.note) {
        // 嵌套格式：{note: {...}}
        updatedNote = response.data.note;
      } else {
        throw new Error('Unexpected API response format');
      }

      if (updatedNote) {
        // 更新选中的笔记
        setSelectedNote(updatedNote);

        // 重新获取主题层级信息
        await fetchNoteTopicHierarchy(selectedNote.id);

        // 强制更新全局状态中的选中笔记
        updateGlobalState({
          selectedNote: updatedNote
        }, 'Dashboard-refreshNote');

        // 通知笔记状态变化，触发UI刷新
        updateGlobalState({
          noteStateChange: {
            changeType: 'refresh',
            timestamp: Date.now(),
            noteId: updatedNote.id
          }
        }, 'Dashboard-refreshNoteStateChange');

        // 强制更新realContent，确保UI显示最新内容
        // 使用统一的 loadNoteContent 函数加载笔记内容
        setIsLoadingContent(true);
        try {
          const result = await loadNoteContent(updatedNote, apiClient, t, isFilePath);
          setRealContent(result.content);
          setIsLoadingContent(result.needsLoading);
        } catch (error) {
          console.error('[Dashboard] 加载笔记内容失败:', error);
          setRealContent(t('dashboard:no_content', { defaultValue: 'No content' }));
          setIsLoadingContent(false);
        }

        message.success(t('messages:note_updated', { defaultValue: 'Note updated' }));

        console.log('[Dashboard] 笔记刷新成功:', {
          noteId: selectedNote.id,
          oldTitle: selectedNote.title,
          newTitle: updatedNote.title,
          oldContent: selectedNote.content?.substring(0, 100),
          newContent: updatedNote.content?.substring(0, 100),
          realContentUpdated: true,
          noteListRefreshTriggered: true
        });
      } else {
        message.error(t('messages:error_loading', { defaultValue: 'Error loading data' }));
      }
    } catch (error) {
      console.error('[Dashboard] 刷新笔记失败:', error);
      message.error(t('messages:error_loading', { defaultValue: 'Error loading data' }) + '：' + (error.response?.data?.message || error.message));
    }
  }, [selectedNote, setSelectedNote, fetchNoteTopicHierarchy, updateGlobalState]);

  // 包装创建模态框取消处理函数
  const handleCreateModalCancel = () => {
    // 立即设置模态框为不可见
    setIsCreateModalVisible(false);

    // 重置批量创建状态
    setIsBatchCreating(false);
    setIsCreatingNote(false);
    setCreateTimer(0);
    if (timerInterval) {
      clearInterval(timerInterval);
      setTimerInterval(null);
    }

    originalHandleCreateModalCancel();

    resetCreateNoteSelectors();
    // 清空文件选择
    setSelectedFiles([]);
    setUploadedFileUrls([]);
    // 重置手动输入内容
    setManualInputContent('');
  };

  // 包装打开创建模态框的函数；可传入 enableAIWriter（缺省 false）控制是否弹出 AI Writer 助手
  const handleOpenCreateModal = async (enableAIWriter = false) => {
    setCreateModalEnableAIWriter(enableAIWriter);
    setIsCreateModalVisible(true);
    createForm.resetFields();
    linkForm.resetFields();
    setUploadedFileUrls([]); // 打开时清空
    await initializeCreateNoteSelectors();
  };

  // 使用包装后的创建函数
  const handleCreateNote = wrappedHandleCreateNote;
  const handleCreateFromLink = wrappedHandleCreateFromLink;

  // 创建主题处理函数
  const topicHandlers = createTopicHandlers({
    setExpandedTopics: (value) => {
      // 包装函数，处理函数式更新
      if (typeof value === 'function') {
        const currentArray = getExpandedTopicsArray();
        const newValue = value(currentArray);
        setExpandedTopicsArray(newValue);
      } else {
        setExpandedTopicsArray(value);
      }
    }, // For collaborative topics
    setEditingTopic, // For collaborative topics  
    setEditingTopicName, // For collaborative topics
    setEditingTopicContext, // For collaborative topics
    setPrivateTopics, // Pass the actual function from context
    fetchPrivateKBTopics, // Pass the actual function from context
    setIsCreateTopicVisible,
    createTopicForm,
    setIsCreateSubTopicVisible,
    createSubTopicForm,
    setSelectedParentTopic,
    setCollaborativeKBTopics, // Pass the actual function from context
    refreshKnowledgeBasesAndTopics
  });

  const {
    toggleTopicExpanded,
    handleRenameTopic,
    handleConfirmRename,
    handleCancelRename,
    handleDeleteTopic,
    handleCreateTopic,
    handleCancelCreateTopic,
    handleCreateSubTopic,
    handleCancelCreateSubTopic,
    handleStartCreateSubTopic
  } = topicHandlers;

  // 主题编辑处理函数
  const handleEditTopic = (topic, context) => {
    setEditingTopic(topic);
    editTopicForm.setFieldsValue({
      name: topic.name,
      description: topic.description || '',
      tagnamelist: topic.tagnamelist || ''
    });
    setEditingTopicContext(context);
    setIsEditTopicVisible(true);
  };

  const handleConfirmEditTopic = async (values) => {
    try {
      if (editingTopicContext && editingTopicContext.type === 'collaborative') {
        // 协作库主题：调用协作库更新API
        const response = await apiClient.put(`/users/knowledge-base/collaborative/${editingTopicContext.kbId}/topics/${editingTopic.topic_id}`, {
          name: values.name,
          description: values.description,
          tagnamelist: values.tagnamelist
        });

        console.log('更新协作库主题成功:', response.data);
        message.success(t('messages:topic_rename_success', { defaultValue: 'Topic renamed successfully' }));

        // 刷新协作库主题数据 - 先更新协作库Context中的主题数据
        try {
          console.log('[handleConfirmEditTopic] 更新协作库主题数据:', editingTopicContext.kbId);
          const updatedTopics = await apiHelpers.fetchCollaborativeKBTopics(editingTopicContext.kbId);
          setCollaborativeKBTopics(prev => {
            const newTopics = {
              ...prev,
              [editingTopicContext.kbId]: updatedTopics
            };
            console.log('[handleConfirmEditTopic] 协作库主题数据已更新:', {
              kbId: editingTopicContext.kbId,
              oldTopics: prev[editingTopicContext.kbId]?.length || 0,
              newTopics: updatedTopics?.length || 0,
              updatedTopicNames: updatedTopics?.map(t => t.name) || []
            });
            return newTopics;
          });

          // 更新主题笔记数量统计 - 使用协作库Context中的方法
          if (updatedTopics && updatedTopics.length > 0) {
            try {
              console.log('[handleConfirmEditTopic] 开始更新主题笔记数量统计...');
              await fetchTopicNotesCountForTree(updatedTopics);
              console.log('[handleConfirmEditTopic] 主题笔记数量统计更新完成');
            } catch (error) {
              console.error('[handleConfirmEditTopic] 更新主题笔记数量失败:', error);
            }
          }

          // 强制触发UI重新渲染 - 通过更新全局状态
          updateGlobalState({
            noteStateChange: {
              changeType: 'collaborative_topic_edit',
              timestamp: Date.now(),
              kbId: editingTopicContext.kbId,
              topicId: editingTopic.topic_id
            }
          }, 'Dashboard-collaborativeTopicEdit');

        } catch (error) {
          console.error('[handleConfirmEditTopic] 更新协作库主题数据失败:', error);
        }

        // 延迟刷新整个知识库和主题数据，确保协作库主题数据更新不被覆盖
        setTimeout(async () => {
          console.log('[handleConfirmEditTopic] 延迟刷新整个知识库和主题数据...');
          await refreshKnowledgeBasesAndTopics();
        }, 100);
      } else {
        // 私有库主题：调用私有库更新API
        const response = await apiClient.put(`/users/knowledge-base/private/topics/${editingTopic.topic_id}`, {
          name: values.name,
          description: values.description,
          tagnamelist: values.tagnamelist
        });

        console.log('更新私有库主题成功:', response.data);
        message.success(t('messages:topic_rename_success', { defaultValue: 'Topic renamed successfully' }));

        // 更新本地状态 - 使用topicTreeHelpers更新主题树
        if (privateTopics) {
          const updateTopicInTree = (topics, topicId, updates) => {
            return topics.map(topic => {
              if (topic.topic_id === topicId) {
                return { ...topic, ...updates };
              }
              if (topic.children && topic.children.length > 0) {
                return {
                  ...topic,
                  children: updateTopicInTree(topic.children, topicId, updates)
                };
              }
              return topic;
            });
          };

          setPrivateTopics(prev => updateTopicInTree(prev, editingTopic.topic_id, {
            name: values.name,
            description: values.description,
            tagnamelist: values.tagnamelist
          }));
        }

        // 同时刷新数据以确保同步
        await refreshKnowledgeBasesAndTopics();
      }

      setIsEditTopicVisible(false);
      setEditingTopic(null);
      setEditingTopicContext(null);
      editTopicForm.resetFields();
    } catch (error) {
      console.error('更新主题失败:', error);
      message.error(t('messages:save_failed', { defaultValue: 'Save failed' }) + ': ' + (error.response?.data?.message || error.message));
    }
  };

  const handleCancelEditTopic = () => {
    setIsEditTopicVisible(false);
    setEditingTopic(null);
    setEditingTopicContext(null);
    editTopicForm.resetFields();
  };

  // Missing handler functions for DashboardSidebar
  const onToggleExpanded = useCallback((topicId) => {
    console.log('[onToggleExpanded] 切换主题展开状态:', topicId);
    const currentExpandedTopics = getExpandedTopicsArray();
    const currentArray = Array.isArray(currentExpandedTopics) ? currentExpandedTopics : [];

    console.log('[onToggleExpanded] 当前展开状态:', currentArray);

    if (currentArray.includes(topicId)) {
      const newArray = currentArray.filter(id => id !== topicId);
      console.log('[onToggleExpanded] 收起主题，新状态:', newArray);
      setExpandedTopicsArray(newArray);
    } else {
      const newArray = [...currentArray, topicId];
      console.log('[onToggleExpanded] 展开主题，新状态:', newArray);
      setExpandedTopicsArray(newArray);
    }
  }, []);

  const onToggleCollaborativeKBExpanded = useCallback(async (kbId) => {
    console.log('[前端] 切换协作库展开状态:', kbId);

    // 检查当前状态
    const currentExpandedCollaborativeKBs = getExpandedCollaborativeKBsArray();
    const currentArray = Array.isArray(currentExpandedCollaborativeKBs) ? currentExpandedCollaborativeKBs : [];
    const isCurrentlyExpanded = currentArray.includes(kbId);

    // 直接更新全局状态中的展开状态
    if (currentArray.includes(kbId)) {
      // 如果已展开，则收起（删除）
      const newArray = currentArray.filter(id => id !== kbId);
      setExpandedCollaborativeKBsArray(newArray);
      // 同步更新本地状态
      setExpandedCollaborativeKBs(newArray);
    } else {
      // 如果未展开，则展开（添加）
      const newArray = [...currentArray, kbId];
      setExpandedCollaborativeKBsArray(newArray);
      // 同步更新本地状态
      setExpandedCollaborativeKBs(newArray);
    }

    // 如果正在展开，获取主题数据
    if (!isCurrentlyExpanded) {
      console.log('[前端] 协作库展开，获取主题数据:', kbId);
      // 从协作库 context 获取主题数据
      if (fetchCollaborativeKBTopics) {
        await fetchCollaborativeKBTopics(kbId);
      }
    }

    // 注意：展开协作库只负责显示主题树，不自动选中知识库
    // 用户需要单独点击协作库标题来选中知识库并查看笔记
  }, [fetchCollaborativeKBTopics]);

  const onSetIsCreateCollaborativeVisible = useCallback((visible) => {
    console.log('Set create collaborative visible:', visible);
    setIsCreateCollaborativeVisible(visible);
  }, [setIsCreateCollaborativeVisible]);

  // 计算所有协作库的总笔记数量（用于显示 "Collaborative Libraries" 总数）
  const calculateTotalCollaborativeKBNotesCount = useCallback(() => {
    // 直接从 notesStats 中读取协作库笔记总数
    if (notesStats) {
      return notesStats.collaborativeNotes || 0;
    }
    return 0;
  }, [notesStats]);

  // 计算单个协作库的笔记数量（用于显示单个协作库的笔记数）
  const calculateCollaborativeKBNotesCount = useCallback((kbId) => {
    // 从缓存中读取，如果缓存中没有则返回0（会在useEffect中异步加载）
    return kbNotesCountCache[kbId] || 0;
  }, [kbNotesCountCache]);

  // 当协作库列表变化时，重新获取笔记数量
  useEffect(() => {
    if (collaborativeKBs && collaborativeKBs.length > 0) {
      fetchAllCollaborativeKBNotesCount();
    }
  }, [collaborativeKBs, fetchAllCollaborativeKBNotesCount]);

  const calculatePrivateKBNotesCount = useCallback(() => {
    // 直接从 notesStats 中读取私有库笔记总数
    if (notesStats) {
      return notesStats.privateNotes || 0;
    }
    return 0;
  }, [notesStats]);

  // 创建协作库处理函数 - now handled by context
  const collaborativeHandlers = createCollaborativeHandlers({
    setIsCreateCollaborativeVisible: () => { }, // Handled by context
    createCollaborativeForm: null,
    fetchUserCollaborativeKBs: () => { }, // Handled by context
    refreshKnowledgeBasesAndTopics
  });

  const {
    handleCreateCollaborative,
    handleCancelCreateCollaborative
  } = collaborativeHandlers;

  // 渲染主题树的递归组件
  const renderTopicTree = useCallback((topics, level = 0, context = { type: 'private', kbId: null }, expandedTopics = null) => {
    if (!topics || topics.length === 0) {
      return null;
    }

    // 如果没有传入展开状态，则获取当前状态
    const currentExpandedTopics = expandedTopics || getExpandedTopicsArray();

    // 调试日志：检查展开状态
    console.log('[renderTopicTree] 渲染主题树:', {
      level,
      context,
      topicsCount: topics.length,
      currentExpandedTopics,
      expandedTopics
    });

    // 过滤主题：根据context类型使用不同的配置变量
    // 私有库使用 DonotDisplayZeroCountTopic，协作库使用 DonotDisplayZeroCountTopicForCollaborativeKB
    const shouldFilterZeroCount = context.type === 'collaborative'
      ? DonotDisplayZeroCountTopicForCollaborativeKB
      : DonotDisplayZeroCountTopic;

    const filteredTopics = topics.filter(topic => {
      if (!shouldFilterZeroCount) {
        return true; // 如果设置为false，显示所有主题
      }

      const notesCount = topicNotesCount[topic.topic_id] || 0;
      const isDefaultTopic = topic.is_forever === true || topic.default === true;

      // 如果是缺省主题（is_forever=true 或 default=true），总是显示
      if (isDefaultTopic) {
        return true;
      }

      // 否则，只显示笔记数量大于0的主题
      return notesCount > 0;
    });

    if (filteredTopics.length === 0) return null;

    // 根据level计算paddingLeft，WLAN的手工配置处
    return filteredTopics.map(topic => {
      // 调试日志：检查每个主题的展开状态
      const isExpanded = currentExpandedTopics.includes(topic.topic_id);
      console.log(`[renderTopicTree] 主题 ${topic.name} (${topic.topic_id}):`, {
        level,
        isExpanded,
        hasChildren: topic.children && topic.children.length > 0,
        childrenCount: topic.children ? topic.children.length : 0
      });

      return (
        <div key={topic.topic_id} style={{ width: '100%' }}>
          <div
            className={`folder-item ${getSelectedTopic() && getSelectedTopic().topic_id === topic.topic_id ? 'active' : ''}`}
            style={{
              display: 'flex',
              alignItems: 'center',
              paddingLeft: level * 8,
              width: '100%',
              minHeight: '26px' /* 降低20%：从32px改为26px */
            }}
          >
            <div style={{
              width: '16px',
              display: 'flex',
              justifyContent: 'center',
              marginRight: '4px',
              flexShrink: 0
            }}>
              {topic.children && topic.children.length > 0 ? (
                <Button
                  type="text"
                  size="small"
                  icon={isExpanded ? <MinusOutlined /> : <PlusOutlined />}
                  onClick={() => {
                    console.log(`[renderTopicTree] 点击展开/折叠按钮: ${topic.name} (${topic.topic_id}), 当前状态: ${isExpanded}`);
                    toggleTopicExpanded(topic.topic_id);
                  }}
                  style={{
                    padding: '0',
                    width: '16px',
                    height: '16px',
                    fontSize: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                />
              ) : (
                <div style={{ width: '16px', height: '16px' }} />
              )}
            </div>
            <div
              className="folder-content"
              style={{
                display: 'flex',
                alignItems: 'center',
                flex: 1,
                minWidth: 0,
                justifyContent: 'space-between'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
                {editingTopic === topic.topic_id ? (
                  <Input
                    value={editingTopicName}
                    onChange={(e) => setEditingTopicName(e.target.value)}
                    onPressEnter={() => handleConfirmRename(topic.topic_id, editingTopicName, editingTopicContext)}
                    onBlur={handleCancelRename}
                    style={{
                      fontSize: '13px',
                      padding: '2px 6px',
                      height: '22px'
                    }}
                    autoFocus
                  />
                ) : (
                  <>
                    <span
                      className="folder-title"
                      style={{
                        flex: 1,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        fontSize: '13px',
                        cursor: 'pointer',
                        maxWidth: '20ch' // 限制最大宽度为20个字符
                      }}
                      title={topic.name}
                      onClick={() => {
                        console.log(`[renderTopicTree] 点击主题标题: ${topic.name} (${topic.topic_id})`);
                        handleTopicClick(topic, context);
                      }}
                    >
                      <TranslateText text={topic.name} />
                    </span>
                  </>
                )}
              </div>

              {/* 笔记数量和菜单区域 - 右对齐 */}
              <div style={{ display: 'flex', alignItems: 'center', flexShrink: 0, gap: '4px' }}>
                {/* 笔记数量显示 */}
                <span
                  className="folder-count"
                  style={{
                    fontSize: '11px',
                    color: '#444444',
                    cursor: 'pointer',
                    textAlign: 'right',
                    minWidth: '50px'
                  }}
                  onClick={() => {
                    console.log(`[renderTopicTree] 点击笔记数量: ${topic.name} (${topic.topic_id})`);
                    handleTopicClick(topic, context);
                  }}
                >
                  ({topicNotesCount[topic.topic_id] || 0})
                </span>

                {/* 操作菜单 - 竖形菜单，位于笔记数量右侧；由 REACT_APP_ENABLE_EDIT_TOPIC 控制；不显示时保留占位 */}
                {editingTopic !== topic.topic_id ? (
                  enableEditTopic ? (
                    <Dropdown
                      menu={{
                        items: [
                          // 只有没有父主题的主题才显示"新建子主题"选项
                          ...(!topic.super_id ? [{
                            key: 'new-subtopic',
                            label: t('dashboard:actions.create_subtopic', { defaultValue: 'Create Subtopic' }),
                            icon: <PlusOutlined />,
                            onClick: () => {
                              if (context.type === 'collaborative') {
                                // 协作库子主题创建功能
                                console.log('协作库子主题创建功能 - 设置父主题:', topic, 'context:', context);
                                // 将context信息附加到topic对象上
                                const topicWithContext = { ...topic, context };
                                setSelectedParentTopic(topicWithContext);
                                setIsCreateSubTopicVisible(true);
                              } else {
                                // 私有库主题：使用原有逻辑
                                handleStartCreateSubTopic(topic);
                              }
                            }
                          }] : []),
                          // 只有在有"新建子主题"选项时才显示分隔线
                          ...(!topic.super_id ? [{ type: 'divider' }] : []),
                          {
                            key: 'edit',
                            label: t('common:actions.edit', { defaultValue: 'Edit' }),
                            icon: <EditOutlined />,
                            onClick: () => handleEditTopic(topic, context)
                          },
                          {
                            key: 'delete',
                            label: t('common:actions.delete', { defaultValue: 'Delete' }),
                            icon: <DeleteOutlined />,
                            onClick: () => {
                              console.log('[renderTopicTree] 删除按钮被点击:', {
                                context,
                                topic: {
                                  topic_id: topic.topic_id,
                                  name: topic.name
                                }
                              });

                              if (context.type === 'collaborative') {
                                console.log('[renderTopicTree] 调用协作库删除API');
                                // 协作库主题：调用协作库删除API
                                handleDeleteCollaborativeTopic(context.kbId, topic.topic_id, topic.name, refreshKnowledgeBasesAndTopics);
                              } else {
                                console.log('[renderTopicTree] 调用私有库删除API');
                                // 私有库主题：传递完整的topic对象
                                handleDeleteTopic(topic);
                              }
                            },
                            danger: true
                          }
                        ]
                      }}
                      trigger={['click']}
                      placement="bottomRight"
                    >
                      <Button
                        type="text"
                        size="small"
                        style={{
                          padding: '0 4px',
                          fontSize: '14px',
                          height: '20px',
                          marginLeft: '4px',
                          fontWeight: 'bold',
                          color: '#444444',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          lineHeight: '1'
                        }}
                        className="topic-menu-button"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <span style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '1.5px',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '12px',
                          fontWeight: 'bold',
                          lineHeight: '1'
                        }}>
                          <span style={{ display: 'block', width: '2px', height: '2px', borderRadius: '50%', backgroundColor: '#444444' }}></span>
                          <span style={{ display: 'block', width: '2px', height: '2px', borderRadius: '50%', backgroundColor: '#444444' }}></span>
                          <span style={{ display: 'block', width: '2px', height: '2px', borderRadius: '50%', backgroundColor: '#444444' }}></span>
                        </span>
                      </Button>
                    </Dropdown>
                  ) : (
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '12px',
                        height: '20px',
                        marginLeft: '4px',
                        flexShrink: 0
                      }}
                      aria-hidden
                    >
                      {'\u00A0'}
                    </span>
                  )
                ) : null}
              </div>
            </div>
          </div>
          {/* 渲染子主题 - 修复展开状态判断 */}
          {topic.children && topic.children.length > 0 && isExpanded && (
            <div style={{ width: '100%' }}>
              {renderTopicTree(topic.children, level + 1, context, currentExpandedTopics)}
            </div>
          )}
        </div>
      );
    });
  }, [editingTopic, editingTopicName, editingTopicContext, topicNotesCount, handleTopicClick, toggleTopicExpanded, handleConfirmRename, handleCancelRename, handleEditTopic, handleStartCreateSubTopic, handleDeleteCollaborativeTopic, handleDeleteTopic, refreshKnowledgeBasesAndTopics, setSelectedParentTopic, setIsCreateSubTopicVisible]); // 移除 selectedTopic 和 expandedTopics 依赖，使用全局状态

  // Auth check
  if (!isAuthenticated || !token) {
    return null;
  }

  console.log('Dashboard - user:', user?.username);
  console.log('[Dashboard.js line 2148] searchKeyword:', getSearchKeyword());
  console.log('[Dashboard.js line 2148] searchResults:', searchResults);
  console.log('[Dashboard.js line 2149] displayNotes:', displayNotes);

  // Render a three panel layout:
  // DashboardSidebar, NoteListPanel, RightContentPane + knowledgeCard
  return (
    <div className="clipai-dashboard">
      {/* <FontContrastComparison /> */}
      <div className="dashboard-content">
        <Layout style={{ height: '100%', background: 'transparent' }}> {/* Outermost Layout for the three panes */}
          {/* 1. Leftmost Fixed Sider */}
          <DashboardSidebar
            leftmostSiderWidth={leftmostSiderWidth}
            user={user}
            quickActions={quickActions}
            selectedFolder={getSelectedFolder()}
            handleOpenCreateModal={handleOpenCreateModal}
            handleQuickActionClick={handleQuickActionClick}
            privateTopics={privateTopics}
            expandedTopics={expandedTopics}
            hasPrivateKB={hasPrivateKB}
            editingTopic={editingTopic}
            editingTopicName={editingTopicName}
            selectedTopic={getSelectedTopic()}
            onTopicClick={handleTopicClick}
            onToggleExpanded={onToggleExpanded}
            onEditTopic={handleEditTopic}
            onConfirmEditTopic={handleConfirmEditTopic}
            onCancelEditTopic={handleCancelEditTopic}
            renderTopicTree={renderTopicTree}
            collaborativeKBs={collaborativeKBs}
            expandedCollaborativeKBsArray={expandedCollaborativeKBs}
            onToggleCollaborativeKBExpanded={onToggleCollaborativeKBExpanded}
            onCollaborativeKBClick={handleKBClick}
            onStartCreateCollaborativeKBTopic={handleStartCreateCollaborativeKBTopic}
            onRenameCollaborativeKB={handleRenameCollaborativeKB}
            onConfirmRenameCollaborativeKB={handleConfirmRenameCollaborativeKB}
            onCancelRenameCollaborativeKB={handleCancelRenameCollaborativeKB}
            onShareCollaborativeKB={handleShareCollaborativeKB}
            onViewMembers={handleViewMembers}
            onLeaveCollaborativeKB={handleLeaveCollaborativeKB}
            onSetIsCreateCollaborativeVisible={onSetIsCreateCollaborativeVisible}
            calculateCollaborativeKBNotesCount={calculateCollaborativeKBNotesCount}
            calculateTotalCollaborativeKBNotesCount={calculateTotalCollaborativeKBNotesCount}
            notesStats={notesStats}
            calculatePrivateKBNotesCount={calculatePrivateKBNotesCount}
            selectedKB={getSelectedKB()}

            // Props for new note creation modals
            createModalEnableAIWriter={createModalEnableAIWriter}
            isCreateModalVisible={isCreateModalVisible}
            isCreatingNote={isCreatingNote}
            isBatchCreating={isBatchCreating}
            batchResult={batchResult}
            selectedFiles={selectedFiles}
            createNoteKnowledgeBase={createNoteKnowledgeBase}
            createAvailableKnowledgeBases={createAvailableKnowledgeBases}
            manualInputContent={manualInputContent}
            createTimer={createTimer}
            isCreateTopicVisible={isCreateTopicVisible}
            isCreateSubTopicVisible={isCreateSubTopicVisible}
            selectedParentTopic={selectedParentTopic}
            isEditTopicVisible={isEditTopicVisible}
            editingTopicContext={editingTopicContext}
            createForm={createForm}
            linkForm={linkForm}
            createTopicForm={createTopicForm}
            createSubTopicForm={createSubTopicForm}
            editTopicForm={editTopicForm}
            handleCreateModalCancel={handleCreateModalCancel}
            handleCreateNote={wrappedHandleCreateNote}
            handleCreateFromLink={wrappedHandleCreateFromLink}
            handleCreateNoteKnowledgeBaseChange={handleCreateNoteKnowledgeBaseChange}
            setManualInputContent={setManualInputContent}
            handleCreateTopic={handleCreateTopic}
            handleCancelCreateTopic={handleCancelCreateTopic}
            handleCreateSubTopic={handleCreateSubTopic}
            handleCancelCreateSubTopic={handleCancelCreateSubTopic}
            handleConfirmEditTopic={handleConfirmEditTopic}
            handleCancelEditTopic={handleCancelEditTopic}
            setIsCreateTopicVisible={setIsCreateTopicVisible}
            handleCreateCollaborative={contextHandleCreateCollaborative}
            isCreateCollaborativeVisible={isCreateCollaborativeVisible}
            setIsCreateCollaborativeVisible={setIsCreateCollaborativeVisible}
            setSelectedFiles={setSelectedFiles}
            setIsCreateModalVisible={setIsCreateModalVisible}
            setIsBatchCreating={setIsBatchCreating}
            setIsCreatingNote={setIsCreatingNote}
          />

          {/* Nested Layout for the two resizable panes (Note List and Preview) */}
          <Layout ref={resizablePanesContainerRef} style={{ height: '100%', background: 'transparent' }}>
            {/* 2. Middle Resizable Sider (Note List) */}
            <Sider
              width={isRightPanelVisible ? noteListWidth : '100%'}
              theme="light"
              style={{
                background: '#f5f5f5',
                borderRight: 'none',
                flex: isRightPanelVisible ? 'none' : 1,
                maxWidth: isRightPanelVisible ? noteListWidth : '100%'
              }}
              collapsible={false}
            >
              <NoteListPanel
                notesStats={notesStats}
                isRightPanelVisible={isRightPanelVisible}
                onToggleRightPanel={() => setIsRightPanelVisible(!isRightPanelVisible)}
                rightPanelMode={rightPanelMode}
                onRightPanelModeChange={(mode) => {
                  setRightPanelMode(mode);
                  // 检查是否是搜索触发的模式切换
                  if (isSearchTriggeredRef.current) {
                    // 如果是搜索触发的，不清除 qaInitialQuestion
                    console.log('[Dashboard] 搜索触发的模式切换，保留 QA 初始问题');
                    isSearchTriggeredRef.current = false; // 重置标志
                  } else {
                    // 如果是手动切换模式，清除初始问题
                    console.log('[Dashboard] 手动模式切换，清除 QA 初始问题');
                    setQaInitialQuestion(null);
                  }
                  // 清除自动触发标志
                  setShouldAutoTriggerQA(false);
                }}
                onSearchTrigger={(searchKeyword) => {
                  // 搜索时设置标志，表示接下来的模式切换是搜索触发的
                  isSearchTriggeredRef.current = true;
                  // 搜索时设置自动触发标志
                  setShouldAutoTriggerQA(true);
                  // 使用传入的搜索关键词作为初始问题
                  const trimmedKeyword = searchKeyword && searchKeyword.trim() ? searchKeyword.trim() : null;
                  if (trimmedKeyword) {
                    console.log('[Dashboard] 搜索触发，设置 QA 初始问题:', trimmedKeyword);
                    lastSearchKeywordRef.current = trimmedKeyword;
                    setQaInitialQuestion(trimmedKeyword);
                  }
                }}
              />
            </Sider>

            {/* 可拖动的分割线 - 仅在右侧面板可见时显示 */}
            {isRightPanelVisible && (
              <div
                style={{
                  width: '8px',
                  cursor: 'col-resize',
                  userSelect: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: isDragging ? '#f0f0f0' : 'transparent',
                  transition: 'background-color 0.2s',
                  position: 'relative',
                }}
                onMouseDown={handleMouseDown}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                onMouseLeave={(e) => {
                  if (!isDragging) e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <div
                  style={{
                    width: '1px',
                    height: '100%',
                    backgroundColor: isDragging ? '#1890ff' : '#d9d9d9',
                    transition: isDragging ? 'none' : 'background-color 0.2s',
                  }}
                />
              </div>
            )}

            {/* 3. Right Content Pane (Preview) - 条件渲染 */}
            {isRightPanelVisible && (
              <Content style={{
                background: '#ffffff',
                borderRadius: '0 12px 12px 0',
                overflow: 'auto',
                minWidth: MIN_PREVIEW_WIDTH,
                flex: 1
              }}>
                <RightContentPane
                  selectedNote={selectedNote}
                  activeTabKey={activeTabKey}
                  onTabChange={handleTabChange}
                  onNoteClick={handleNoteClick}
                  onQATopicClick={handleQATopicClick}
                  currentFilterState={{
                    selectedFolder: getSelectedFolder(),
                    selectedTopic: getSelectedTopic(),
                    selectedKB: getSelectedKB(),
                    selectedTag: getSelectedTag(),
                    searchKeyword: getSearchKeyword()
                  }}
                  rightPanelMode={rightPanelMode}
                  onRightPanelModeChange={setRightPanelMode}
                  isRightPanelVisible={isRightPanelVisible}
                  onToggleRightPanel={() => setIsRightPanelVisible(!isRightPanelVisible)}
                >
                  {rightPanelMode === 'ai-ask' ? (
                    <NoteListAsk
                      onClose={() => setIsRightPanelVisible(false)}
                      onBackToSummary={() => setRightPanelMode('note-detail')}
                      qaContentRef={qaContentRef}
                      width="100%"
                      embedded={true}
                      initialQuestion={qaInitialQuestion}
                      selectedNote={selectedNote}
                    />
                  ) : rightPanelMode === 'aiwriter' ? (
                    <AIWriterPanel
                      selectedNote={selectedNote}
                      currentFilterState={{
                        selectedFolder: getSelectedFolder(),
                        selectedTopic: getSelectedTopic(),
                        selectedKB: getSelectedKB(),
                        selectedTag: getSelectedTag(),
                        searchKeyword: getSearchKeyword()
                      }}
                      onBack={() => setRightPanelMode('note-detail')}
                    />
                  ) : (
                    <KnowledgeCard
                      selectedNote={selectedNote}
                      isEditing={isEditing}
                      editTitle={editTitle}
                      setEditTitle={setEditTitle}
                      editSummary={editSummary}
                      setEditSummary={setEditSummary}
                      editContent={editContent}
                      setEditContent={setEditContent}
                      user={user}
                      handleSave={handleSave}
                      handleCancel={handleCancel}
                      handleDeleteNote={handleDeleteNote}
                      handleRefreshNote={handleRefreshNote}
                      handlePin={handlePin}
                      handleEdit={handleEdit}
                      editTooltipVisible={editTooltipVisible}
                      setEditTooltipVisible={setEditTooltipVisible}
                      isAddingTag={isAddingTag}
                      setIsAddingTag={setIsAddingTag}
                      handleFetchUserHistoryTags={handleFetchUserHistoryTags}
                      newTagName={newTagName}
                      setNewTagName={setNewTagName}
                      handleAddTag={handleAddTag}
                      userHistoryTags={userHistoryTags}
                      setUserHistoryTags={setUserHistoryTags}
                      isLoadingHistoryTags={isLoadingHistoryTags}
                      updateGlobalState={updateGlobalState}
                      setIsUserSelectedNote={setIsUserSelectedNote}
                      setSelectedNote={setSelectedNote}
                      isContentExpanded={isContentExpanded}
                      setIsContentExpanded={setIsContentExpanded}
                      isLoadingContent={isLoadingContent}
                      realContent={realContent}
                      noteTopicHierarchy={noteTopicHierarchy}
                      fetchAllKnowledgeBases={fetchAllKnowledgeBases}
                      refreshKnowledgeBasesAndTopics={refreshKnowledgeBasesAndTopics}
                      onNoteClick={handleNoteClick}
                      onQATopicClick={handleQATopicClick}
                      onTabChange={handleTabChange}
                      currentFilterState={{
                        selectedFolder: getSelectedFolder(),
                        selectedTopic: getSelectedTopic(),
                        selectedKB: getSelectedKB(),
                        selectedTag: getSelectedTag(),
                        searchKeyword: getSearchKeyword()
                      }}
                      isRightPanelVisible={isRightPanelVisible}
                      onToggleRightPanel={() => setIsRightPanelVisible(!isRightPanelVisible)}
                      onOpenCreateModalWithAIWriter={() => handleOpenCreateModal(true)}
                      onRightPanelModeChange={setRightPanelMode}
                    />
                  )}
                </RightContentPane>
              </Content>
            )}
          </Layout> {/* End of Nested Layout for resizable panes */}
        </Layout> {/* End of Outermost Layout for the three panes */}
      </div>
      {/* 新增：显示已上传文件URL */}
      {uploadedFileUrls.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <Text type="secondary">{t('dashboard:uploaded_file_urls', { defaultValue: 'Uploaded file URLs:' })}</Text>
          <ul>
            {uploadedFileUrls.map(url => (
              <li key={url}>
                <a href={url} target="_blank" rel="noopener noreferrer">{url}</a>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

// Wrapper component to provide all contexts
const Dashboard = () => {
  return (
    <PrivateKnowledgeBaseProvider>
      <CollaborativeKnowledgeBaseProvider>
        <NoteListProvider>
          <DashboardContent />
        </NoteListProvider>
      </CollaborativeKnowledgeBaseProvider>
    </PrivateKnowledgeBaseProvider>
  );
};

export default Dashboard;
